import { sanitizeHtml } from '../utils/sanitizeHtml';
import React, { useState, useEffect } from 'react';
import BBALoader from '../components/BBALoader';
import { useLocation, useNavigate } from 'react-router-dom';
import { CheckCircle, AlertCircle, Calendar, FolderPlus, Shield, FileText, Clock } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useOnboarding } from '../contexts/OnboardingContext';
import { doc, updateDoc, collection, getDocs } from 'firebase/firestore';
import { getMyAccountSnapshot } from '../services/firestoreService';
import { db } from '../firebase';
import ESignatureService from '../services/eSignatureService';
import AuditTrailViewer from '../components/AuditTrailViewer';
import { getSharedContractTemplate } from '../services/firebase/contractTemplateService';
import { getNextContractNumber, incrementContractNumber } from '../services/firebase/contractNumberService';
import BackButton from '../components/BackButton';
import '@fontsource/dancing-script';
import PortalBackground from '../components/PortalBackground';

const PortalContract = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { darkMode } = useTheme();
  const { isOnboardingMode, markStepComplete } = useOnboarding();
  const [isScrolledToBottom, setIsScrolledToBottom] = useState(false);
  const [formData, setFormData] = useState({
    companyName: '',
    state: '',
    companyAddress: '',
    city: '',
    zipCode: '',
    businessStructure: '',
    signerName: '',
    signerTitle: '',
    signature: '',
    initial: '',
  });
  const [dynamicFormData, setDynamicFormData] = useState({});
  const [hasAgreed, setHasAgreed] = useState(false);
  const [isSigned, setIsSigned] = useState(false);
  const [error, setError] = useState('');
  const [templateData, setTemplateData] = useState(null);
  const [templateLoading, setTemplateLoading] = useState(true);
  const [templateError, setTemplateError] = useState(null);
  const [contractNumber, setContractNumber] = useState(null);
  const [contractItems, setContractItems] = useState([]);
  
  // E-SIGN Act compliance state
  const [consentData, setConsentData] = useState({
    consentToElectronic: false,
    consentToRecords: false,
    consentTimestamp: null
  });
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [showAuditTrail, setShowAuditTrail] = useState(false);
  const [auditTrailData, setAuditTrailData] = useState(null);
  const [highlightedVariableId, setHighlightedVariableId] = useState(null);
  const [currentInputIndex, setCurrentInputIndex] = useState(0);
  const [variablesInOrder, setVariablesInOrder] = useState([]);

  // Get data from navigation state
  const selectedProgram = location.state?.selectedProgram;
  const selectedTier = location.state?.selectedTier;
  const billingCycle = location.state?.billingCycle;
  const checkoutData = location.state?.checkoutData;
  const customDeliverables = location.state?.customDeliverables;
  const customDeliverableQuantities = location.state?.customDeliverableQuantities;

  // Load contract items from ProgramDetail/CustomProgram deliverables (alaCarteItems)
  const loadContractItemsFromDeliverables = async () => {
    if (!customDeliverables || !Array.isArray(customDeliverables) || customDeliverables.length === 0) {
      return [];
    }
    try {
      const alaCarteSnapshot = await getDocs(collection(db, 'alaCarteItems'));
      const alaCarteItems = alaCarteSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      const quantities = customDeliverableQuantities || {};
      const items = customDeliverables
        .map(itemId => {
          const item = alaCarteItems.find(i => i.id === itemId);
          if (!item) return null;
          const qty = quantities[itemId] ?? 1;
          return { name: item.name || item.itemName || item.title || 'Item', quantity: String(qty) };
        })
        .filter(Boolean);
      return items;
    } catch (err) {
      console.error('Error loading contract items from deliverables:', err);
      return [];
    }
  };

  // Load contract items from subcollection
  const loadContractItems = async () => {
    if (!selectedProgram) return;
    
    try {
      console.log('Loading contract items for program:', selectedProgram);
      console.log('Program name:', selectedProgram.name);
      console.log('Program id:', selectedProgram.id);
      
      // First, let's check what programs exist in the database
      const programsSnapshot = await getDocs(collection(db, 'programs'));
      console.log('Available programs in database:');
      programsSnapshot.docs.forEach(doc => {
        console.log(`- Program ID: "${doc.id}", Name: "${doc.data().name}"`);
      });
      
      // Get the program key from the selected program
      let programKey = selectedProgram.id || selectedProgram.name;
      console.log('Initial program key:', programKey);
      
      // Try to find the matching program in the database
      let matchingProgramDoc = null;
      for (const doc of programsSnapshot.docs) {
        const programData = doc.data();
        if (doc.id === programKey || 
            programData.name === selectedProgram.name ||
            programData.name === programKey) {
          matchingProgramDoc = doc;
          programKey = doc.id; // Use the actual document ID
          console.log('Found matching program with ID:', programKey);
          break;
        }
      }
      
      let items = [];
      if (matchingProgramDoc) {
        // Query contract items subcollection for this program
        const contractItemsSnapshot = await getDocs(collection(db, 'programs', programKey, 'contractItems'));
        console.log('Contract items snapshot size:', contractItemsSnapshot.docs.length);
        
        items = contractItemsSnapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .sort((a, b) => (a.order || 0) - (b.order || 0));
      }
      
      // Fallback: use deliverables from ProgramDetail/CustomProgram when Firestore has no items
      if (items.length === 0 && customDeliverables?.length > 0) {
        console.log('No Firestore contract items; loading from ProgramDetail/CustomProgram deliverables');
        items = await loadContractItemsFromDeliverables();
      }
      
      console.log('Loaded contract items:', items);
      setContractItems(items);
      
    } catch (error) {
      console.error('Error loading contract items:', error);
      setContractItems([]);
    }
  };


  // Load contract template data on component mount
  useEffect(() => {
    const loadTemplate = async () => {
      if (!currentUser) return;
      
      try {
        setTemplateLoading(true);
        setTemplateError(null);
        
        // Load template and contract number
        const [template, nextContractNumber] = await Promise.all([
          getSharedContractTemplate(),
          getNextContractNumber()
        ]);
        
        setTemplateData(template);
        setContractNumber(nextContractNumber);
        
        // Initialize form data with template variables
        if (template.contractElements && template.contractElements.length > 0) {
          initializeFormDataFromTemplate(template.contractElements);
        }
      } catch (err) {
        console.error('Error loading contract template:', err);
        setTemplateError('Failed to load contract template. Please try again.');
      } finally {
        setTemplateLoading(false);
      }
    };

    loadTemplate();
  }, [currentUser]);

  // Load contract items when selectedProgram or customDeliverables changes
  useEffect(() => {
    if (selectedProgram) {
      loadContractItems();
    }
  }, [selectedProgram, customDeliverables, customDeliverableQuantities]);

  // Update variables in order when template data changes
  useEffect(() => {
    if (templateData) {
      const ordered = getClientVariablesInOrder();
      setVariablesInOrder(ordered);
      // Set first variable as highlighted if there are any
      if (ordered.length > 0) {
        setHighlightedVariableId(ordered[0].id);
        setCurrentInputIndex(0);
      }
    }
  }, [templateData]);

  // Auto-scroll to current variable when index changes
  useEffect(() => {
    if (variablesInOrder.length > 0 && currentInputIndex < variablesInOrder.length) {
      const currentVariable = variablesInOrder[currentInputIndex];
      
      // Only highlight and scroll if it's not a consent/agreement checkbox (they don't exist in the document)
      if (!currentVariable.isConsentCheckbox && !currentVariable.isAgreementCheckbox && !currentVariable.isCombinedConsentCheckbox) {
        setHighlightedVariableId(currentVariable.id);
        
        // Scroll to the variable after a short delay to ensure DOM is updated
        setTimeout(() => {
          const variableSpan = document.querySelector(`[data-variable-id="${currentVariable.id}"]`);
          console.log('Auto-scroll looking for variable:', {
            variableId: currentVariable.id,
            variableTitle: currentVariable.variableTitle,
            variableType: currentVariable.type,
            found: !!variableSpan,
            allVariableSpans: document.querySelectorAll('[data-variable-id]').length
          });
          if (variableSpan) {
            variableSpan.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'center',
              inline: 'nearest'
            });
          } else {
            console.warn('Variable span not found for:', currentVariable.id);
          }
        }, 100);
      } else {
        // Clear highlight for consent/agreement checkboxes
        setHighlightedVariableId(null);
      }
    }
  }, [currentInputIndex, variablesInOrder]);

  useEffect(() => {
    // Redirect if no program data
    if (!selectedProgram || !selectedTier || !billingCycle) {
      navigate('/portal/billing');
    }
  }, [selectedProgram, selectedTier, billingCycle, navigate]);

  // Update lead substatus to "Draft" when user navigates to contract page
  useEffect(() => {
    const updateSubstatusToDraft = async () => {
      if (!currentUser || !selectedProgram) return;
      
      try {
        const userEmail = currentUser.email;
        if (userEmail) {
          // The lead account this login is linked to
          let accountsSnapshot = await getMyAccountSnapshot('Leads');
          
          if (!accountsSnapshot.empty) {
            const leadDoc = accountsSnapshot.docs[0];
            const leadData = leadDoc.data();
            
            // Prepopulate form data from lead/account when we have known values
            const discovery = leadData.discovery || {};
            const signedNDA = leadData.signedNDA || {};
            const signedContract = leadData.signedContract || {};
            const contact = leadData.contact || {};
            let companyAddress = leadData.streetAddress || discovery.streetAddress || leadData.location || signedNDA.companyAddress || signedContract.companyAddress || '';
            if (!companyAddress && leadData.address?.street) {
              companyAddress = leadData.address.street;
            }
            
            const prepopulatedData = {
              companyName: leadData.companyName || leadData.company || discovery.companyName || signedNDA.companyName || signedContract.companyName || '',
              state: leadData.state || discovery.state || signedNDA.state || signedContract.state || '',
              companyAddress: companyAddress,
              city: leadData.city || discovery.city || signedNDA.city || signedContract.city || '',
              zipCode: leadData.zipCode || discovery.zipCode || signedNDA.zipCode || signedContract.zipCode || '',
              businessStructure: discovery.businessStructure || leadData.businessStructure || signedNDA.businessStructure || signedContract.businessStructure || '',
              signerName: contact.name || discovery.signerName || (discovery.firstName && discovery.lastName ? `${discovery.firstName} ${discovery.lastName}` : '') || (leadData.firstName && leadData.lastName ? `${leadData.firstName} ${leadData.lastName}` : '') || signedNDA.signerName || signedContract.signerName || '',
              signerTitle: contact.title || discovery.signerTitle || signedNDA.signerTitle || signedContract.signerTitle || '',
            };
            
            if (prepopulatedData.companyName || prepopulatedData.signerName || prepopulatedData.companyAddress) {
              setFormData(prev => ({
                ...prev,
                companyName: prev.companyName || prepopulatedData.companyName,
                state: prev.state || prepopulatedData.state,
                companyAddress: prev.companyAddress || prepopulatedData.companyAddress,
                city: prev.city || prepopulatedData.city,
                zipCode: prev.zipCode || prepopulatedData.zipCode,
                businessStructure: prev.businessStructure || prepopulatedData.businessStructure,
                signerName: prev.signerName || prepopulatedData.signerName,
                signerTitle: prev.signerTitle || prepopulatedData.signerTitle,
              }));
              console.log('Prepopulated Service Agreement form from lead data:', prepopulatedData);
            }
            
            // Only update if lead is in Proposal stage and substatus is not already "Draft" or "Pending Payment"
            if (leadData.stage === 'Proposal' && 
                leadData.substatus !== 'Draft' && 
                leadData.substatus !== 'Pending Payment') {
              const currentDate = new Date().toISOString().split('T')[0];
              const substatusDates = leadData.substatusDates || {};
              substatusDates['Draft'] = currentDate;
              
              // Add to statusHistory for tracking
              const statusHistory = leadData.statusHistory || [];
              const substatusEntry = {
                substatus: 'Draft',
                date: new Date().toISOString(),
                updatedBy: 'System',
                action: 'User navigated to contract page'
              };
              
              await updateDoc(doc(db, 'accounts', leadDoc.id), {
                substatus: 'Draft',
                substatusDates: substatusDates,
                statusHistory: [...statusHistory, substatusEntry],
                lastUpdated: new Date().toISOString()
              });
              
              console.log('Updated lead substatus to Draft (user navigated to contract page)');
            }
          }
        }
      } catch (error) {
        console.error('Error updating lead substatus to Draft:', error);
        // Don't block the page if this fails
      }
    };

    // Only run once when component mounts and we have the necessary data
    if (currentUser && selectedProgram) {
      updateSubstatusToDraft();
    }
  }, [currentUser, selectedProgram]);

  // Initialize form data from template
  const initializeFormDataFromTemplate = (contractElements) => {
    const initialFormData = {};
    
    contractElements.forEach(element => {
      // Direct input field types
      if (['signature', 'initial', 'text_input', 'checkbox', 'date'].includes(element.type)) {
        if (element.assignedTo === 'Client') {
          // Initialize with appropriate default values
          if (element.type === 'checkbox') {
            initialFormData[element.id] = 'false';
          } else {
            initialFormData[element.id] = '';
          }
        }
      }
      
      // Handle variables in text content
      if (element.type === 'text' && element.content) {
        const variables = extractVariablesFromContent(element.content);
        variables.forEach((variable, index) => {
          const variableId = `${element.id}-embedded-${index}`;
          if (variable === 'checkbox') {
            initialFormData[variableId] = 'false';
          } else {
            initialFormData[variableId] = '';
          }
        });
      }
      
      // Handle variables in sub-items
      if (element.subItems && element.subItems.length > 0) {
        element.subItems.forEach(subItem => {
          if (subItem.text) {
            const variables = extractVariablesFromContent(subItem.text);
            variables.forEach((variable, index) => {
              const variableId = `${element.id}-sub-${subItem.id}-embedded-${index}`;
              if (variable === 'checkbox') {
                initialFormData[variableId] = 'false';
              } else {
                initialFormData[variableId] = '';
              }
            });
          }
          
          // Handle nested sub-items
          if (subItem.subItems && subItem.subItems.length > 0) {
            subItem.subItems.forEach(nestedSubItem => {
              if (nestedSubItem.text) {
                const variables = extractVariablesFromContent(nestedSubItem.text);
                variables.forEach((variable, index) => {
                  const variableId = `${element.id}-nested-${nestedSubItem.id}-embedded-${index}`;
                  if (variable === 'checkbox') {
                    initialFormData[variableId] = 'false';
                  } else {
                    initialFormData[variableId] = '';
                  }
                });
              }
            });
          }
        });
      }
    });
    
    setDynamicFormData(initialFormData);
  };

  // Get client variables from template
  const getClientVariables = () => {
    if (!templateData || !templateData.contractElements) {
      console.log('No template data or contract elements found');
      return [];
    }
    
    const clientVariables = [];
    
    // Debug logging
    console.log('getClientVariables - templateData:', templateData);
    console.log('getClientVariables - contractElements:', templateData.contractElements);
    
    templateData.contractElements.forEach(element => {
      console.log('Processing element:', {
        id: element.id,
        type: element.type,
        assignedTo: element.assignedTo,
        variableTitle: element.variableTitle,
        content: element.content
      });
      
      // Direct input field types assigned to Client
      if (['signature', 'initial', 'text_input', 'checkbox', 'date'].includes(element.type) && 
          element.assignedTo === 'Client') {
        console.log('Adding direct Client variable:', element);
        clientVariables.push({
          id: element.id,
          type: element.type,
          variableTitle: element.variableTitle || element.type,
          content: element.content,
          assignedTo: element.assignedTo,
          isDirect: true
        });
      }
      
      // Variables embedded in text content - if element is assigned to Client or has no assignment (default to Client for bracket variables)
      if (element.type === 'text' && element.content && 
          (element.assignedTo === 'Client' || !element.assignedTo)) {
        console.log('Processing text element:', element, 'assignedTo:', element.assignedTo);
        
        // Extract special variables like [SIGNATURE], [INITIAL], [INITIALS], etc.
        const specialPattern = /\[(SIGNATURE|INITIAL|INITIALS|TEXT_INPUT|CHECKBOX|DATE)\]/g;
        let match;
        while ((match = specialPattern.exec(element.content)) !== null) {
          let variableType = match[1].toLowerCase();
          // Handle INITIALS -> initial mapping
          if (variableType === 'initials') {
            variableType = 'initial';
          }
          // Use element assignment directly - default to Client if no assignment
          let assignedTo = element.assignedTo || 'Client';
          
          const newVariable = {
            id: `${element.id}-special-${match.index}`,
            type: variableType,
            variableTitle: variableType === 'text_input' ? 'Text Input' : 
                          variableType === 'checkbox' ? 'Checkbox' :
                          variableType === 'initial' ? 'Initial' :
                          variableType.charAt(0).toUpperCase() + variableType.slice(1),
            content: `[${match[1]}]`,
            assignedTo: assignedTo,
            isDirect: false,
            parentElementId: element.id
          };
          clientVariables.push(newVariable);
          
          // Debug logging for initials
          if (variableType === 'initial') {
            console.log('Found initial variable:', newVariable);
            console.log('Element content:', element.content);
            console.log('Element ID:', element.id);
            console.log('Original match:', match[1]);
          }
        }
        
        // Extract bracket variables like [Company_Name] - only for Client-assigned elements
        // EXCLUDE system variables like [Contract_Number], [Program_Name], etc.
        const bracketPattern = /\[([A-Za-z_][A-Za-z0-9_]*)\]/g;
        while ((match = bracketPattern.exec(element.content)) !== null) {
          const bracketName = match[1];
          
          // Skip system variables - these should not be client input fields
          if (['Contract_Number', 'Program_Name', 'Program_Items', 'Monthly_Price', 'Start_Date', 'Billing_Cycle', 'Billing_Days'].includes(bracketName)) {
            console.log('Skipping system variable:', bracketName);
            continue;
          }
          
          const variableType = getVariableTypeFromBracket(bracketName);
          const displayName = bracketName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
          
          // Use element assignment directly - default to Client if no assignment
          let assignedTo = element.assignedTo || 'Client';
          
          clientVariables.push({
            id: `${element.id}-bracket-${bracketName}-${match.index}`,
            type: variableType,
            variableTitle: displayName,
            content: `[${bracketName}]`,
            assignedTo: assignedTo,
            isDirect: false,
            parentElementId: element.id,
            isBracketVariable: true,
            bracketName: bracketName
          });
        }
      }
      
      // Also check sub-items for embedded variables
      if (element.subItems && element.subItems.length > 0) {
        element.subItems.forEach(subItem => {
          if (subItem.text) {
            // Extract special variables from sub-items
            const specialPattern = /\[(SIGNATURE|INITIAL|INITIALS|TEXT_INPUT|CHECKBOX|DATE)\]/g;
            let match;
            while ((match = specialPattern.exec(subItem.text)) !== null) {
              let variableType = match[1].toLowerCase();
              if (variableType === 'initials') {
                variableType = 'initial';
              }
              let assignedTo = element.assignedTo || 'Client';
              
              clientVariables.push({
                id: `${element.id}-sub-${subItem.id}-special-${match.index}`,
                type: variableType,
                variableTitle: variableType === 'text_input' ? 'Text Input' : 
                              variableType === 'checkbox' ? 'Checkbox' :
                              variableType === 'initial' ? 'Initial' :
                              variableType.charAt(0).toUpperCase() + variableType.slice(1),
                content: `[${match[1]}]`,
                assignedTo: assignedTo,
                isDirect: false,
                parentElementId: element.id,
                parentSubItemId: subItem.id
              });
            }
            
            // Extract bracket variables from sub-items
            const bracketPattern = /\[([A-Za-z_][A-Za-z0-9_]*)\]/g;
            while ((match = bracketPattern.exec(subItem.text)) !== null) {
              const bracketName = match[1];
              
              // Skip system variables - these should not be client input fields
              if (['Contract_Number', 'Program_Name', 'Program_Items', 'Monthly_Price', 'Start_Date', 'Billing_Cycle', 'Billing_Days'].includes(bracketName)) {
                console.log('Skipping system variable in sub-item:', bracketName);
                continue;
              }
              
              const variableType = getVariableTypeFromBracket(bracketName);
              const displayName = bracketName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
              let assignedTo = element.assignedTo || 'Client';
              
              clientVariables.push({
                id: `${element.id}-sub-${subItem.id}-bracket-${bracketName}-${match.index}`,
                type: variableType,
                variableTitle: displayName,
                content: `[${bracketName}]`,
                assignedTo: assignedTo,
                isDirect: false,
                parentElementId: element.id,
                parentSubItemId: subItem.id,
                isBracketVariable: true,
                bracketName: bracketName
              });
            }
          }
          
          // Check nested sub-items
          if (subItem.subItems && subItem.subItems.length > 0) {
            subItem.subItems.forEach(nestedSubItem => {
              if (nestedSubItem.text) {
                // Extract special variables from nested sub-items
                const specialPattern = /\[(SIGNATURE|INITIAL|INITIALS|TEXT_INPUT|CHECKBOX|DATE)\]/g;
                let match;
                while ((match = specialPattern.exec(nestedSubItem.text)) !== null) {
                  let variableType = match[1].toLowerCase();
                  if (variableType === 'initials') {
                    variableType = 'initial';
                  }
                  let assignedTo = element.assignedTo || 'Client';
                  
                  clientVariables.push({
                    id: `${element.id}-nested-${nestedSubItem.id}-special-${match.index}`,
                    type: variableType,
                    variableTitle: variableType === 'text_input' ? 'Text Input' : 
                                  variableType === 'checkbox' ? 'Checkbox' :
                                  variableType === 'initial' ? 'Initial' :
                                  variableType.charAt(0).toUpperCase() + variableType.slice(1),
                    content: `[${match[1]}]`,
                    assignedTo: assignedTo,
                    isDirect: false,
                    parentElementId: element.id,
                    parentSubItemId: subItem.id,
                    parentNestedSubItemId: nestedSubItem.id
                  });
                }
                
                // Extract bracket variables from nested sub-items
                const bracketPattern = /\[([A-Za-z_][A-Za-z0-9_]*)\]/g;
                while ((match = bracketPattern.exec(nestedSubItem.text)) !== null) {
                  const bracketName = match[1];
                  
                  // Skip system variables - these should not be client input fields
                  if (['Contract_Number', 'Program_Name', 'Program_Items', 'Monthly_Price', 'Start_Date', 'Billing_Cycle', 'Billing_Days'].includes(bracketName)) {
                    console.log('Skipping system variable in nested sub-item:', bracketName);
                    continue;
                  }
                  
                  const variableType = getVariableTypeFromBracket(bracketName);
                  const displayName = bracketName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                  let assignedTo = element.assignedTo || 'Client';
                  
                  clientVariables.push({
                    id: `${element.id}-nested-${nestedSubItem.id}-bracket-${bracketName}-${match.index}`,
                    type: variableType,
                    variableTitle: displayName,
                    content: `[${bracketName}]`,
                    assignedTo: assignedTo,
                    isDirect: false,
                    parentElementId: element.id,
                    parentSubItemId: subItem.id,
                    parentNestedSubItemId: nestedSubItem.id,
                    isBracketVariable: true,
                    bracketName: bracketName
                  });
                }
              }
            });
          }
        });
      }
    });
    
    // Filter to only return Client-assigned variables
    const clientOnlyVariables = clientVariables.filter(v => v.assignedTo === 'Client');
    
    console.log('All variables found:', clientVariables);
    console.log('Client-only variables:', clientOnlyVariables);
    console.log('Initial variables found:', clientOnlyVariables.filter(v => v.type === 'initial'));
    console.log('Signature variables found:', clientOnlyVariables.filter(v => v.type === 'signature'));
    return clientOnlyVariables;
  };

  // Get client variables in document order (as they appear in the template)
  const getClientVariablesInOrder = () => {
    if (!templateData || !templateData.contractElements) return [];
    
    let orderedVariables = [];
    const seenVariableIds = new Set();
    
    templateData.contractElements.forEach(element => {
      const elementIndex = templateData.contractElements.indexOf(element);
      
      // Direct input field types assigned to Client
      if (['signature', 'initial', 'text_input', 'checkbox', 'date'].includes(element.type) && 
          element.assignedTo === 'Client') {
        if (!seenVariableIds.has(element.id)) {
          orderedVariables.push({
            id: element.id,
            type: element.type,
            variableTitle: element.variableTitle || element.type,
            content: element.content,
            assignedTo: element.assignedTo,
            isDirect: true,
            elementIndex: elementIndex
          });
          seenVariableIds.add(element.id);
        }
      }
      
      // Variables embedded in text content
      if (element.type === 'text' && element.content && 
          (element.assignedTo === 'Client' || !element.assignedTo)) {
        // Extract special variables like [SIGNATURE], [INITIAL], [INITIALS], etc.
        const specialPattern = /\[(SIGNATURE|INITIAL|INITIALS|TEXT_INPUT|CHECKBOX|DATE)\]/gi;
        let match;
        while ((match = specialPattern.exec(element.content)) !== null) {
          let variableType = match[1].toLowerCase();
          const originalMatch = match[1];
          if (variableType === 'initials') {
            variableType = 'initial';
          }
          let assignedTo = element.assignedTo || 'Client';
          
          let variableTitle;
          if (originalMatch.toUpperCase() === 'INITIALS') {
            variableTitle = 'Initials';
          } else if (originalMatch.toUpperCase() === 'INITIAL') {
            variableTitle = 'Initial';
          } else {
            variableTitle = variableType === 'text_input' ? 'Text Input' : 
                          variableType === 'checkbox' ? 'Checkbox' :
                          variableType.charAt(0).toUpperCase() + variableType.slice(1);
          }
          
          // Use match.index to match the ID format in getClientVariables
          const variableId = `${element.id}-special-${match.index}`;
          if (!seenVariableIds.has(variableId) && assignedTo === 'Client') {
            orderedVariables.push({
              id: variableId,
              type: variableType,
              variableTitle: variableTitle,
              content: `[${match[1]}]`,
              originalContent: originalMatch,
              assignedTo: assignedTo,
              isDirect: false,
              parentElementId: element.id,
              elementIndex: elementIndex,
              matchIndex: match.index
            });
            seenVariableIds.add(variableId);
          }
        }
        
        // Extract bracket variables like [Company_Name]
        const bracketPattern = /\[([A-Za-z_][A-Za-z0-9_]*)\]/g;
        while ((match = bracketPattern.exec(element.content)) !== null) {
          const bracketName = match[1];
          
          // Skip system variables
          if (['Contract_Number', 'Program_Name', 'Program_Items', 'Monthly_Price', 'Start_Date', 'Billing_Cycle', 'Billing_Days'].includes(bracketName)) {
            continue;
          }
          
          const variableType = getVariableTypeFromBracket(bracketName);
          const displayName = bracketName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
          let assignedTo = element.assignedTo || 'Client';
          
          // Use match.index to match the ID format in getClientVariables
          const variableId = `${element.id}-bracket-${bracketName}-${match.index}`;
          if (!seenVariableIds.has(variableId) && assignedTo === 'Client') {
            orderedVariables.push({
              id: variableId,
              type: variableType,
              variableTitle: displayName,
              content: `[${bracketName}]`,
              assignedTo: assignedTo,
              isDirect: false,
              parentElementId: element.id,
              isBracketVariable: true,
              bracketName: bracketName,
              elementIndex: elementIndex,
              matchIndex: match.index
            });
            seenVariableIds.add(variableId);
          }
        }
      }
      
      // Also check sub-items for embedded variables
      if (element.subItems && element.subItems.length > 0) {
        element.subItems.forEach(subItem => {
          if (subItem.text) {
            const specialPattern = /\[(SIGNATURE|INITIAL|INITIALS|TEXT_INPUT|CHECKBOX|DATE)\]/gi;
            let match;
            while ((match = specialPattern.exec(subItem.text)) !== null) {
              let variableType = match[1].toLowerCase();
              const originalMatch = match[1];
              if (variableType === 'initials') {
                variableType = 'initial';
              }
              let assignedTo = element.assignedTo || 'Client';
              
              let variableTitle;
              if (originalMatch.toUpperCase() === 'INITIALS') {
                variableTitle = 'Initials';
              } else if (originalMatch.toUpperCase() === 'INITIAL') {
                variableTitle = 'Initial';
              } else {
                variableTitle = variableType === 'text_input' ? 'Text Input' : 
                              variableType === 'checkbox' ? 'Checkbox' :
                              variableType.charAt(0).toUpperCase() + variableType.slice(1);
              }
              
              // Use match.index to match the ID format in getClientVariables
              const variableId = `${element.id}-sub-${subItem.id}-special-${match.index}`;
              if (!seenVariableIds.has(variableId) && assignedTo === 'Client') {
                orderedVariables.push({
                  id: variableId,
                  type: variableType,
                  variableTitle: variableTitle,
                  content: `[${match[1]}]`,
                  originalContent: originalMatch,
                  assignedTo: assignedTo,
                  isDirect: false,
                  parentElementId: element.id,
                  parentSubItemId: subItem.id,
                  elementIndex: elementIndex,
                  matchIndex: match.index
                });
                seenVariableIds.add(variableId);
              }
            }
            
            const bracketPattern = /\[([A-Za-z_][A-Za-z0-9_]*)\]/g;
            while ((match = bracketPattern.exec(subItem.text)) !== null) {
              const bracketName = match[1];
              
              if (['Contract_Number', 'Program_Name', 'Program_Items', 'Monthly_Price', 'Start_Date', 'Billing_Cycle', 'Billing_Days'].includes(bracketName)) {
                continue;
              }
              
              const variableType = getVariableTypeFromBracket(bracketName);
              const displayName = bracketName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
              let assignedTo = element.assignedTo || 'Client';
              
              // Use match.index to match the ID format in getClientVariables
              const variableId = `${element.id}-sub-${subItem.id}-bracket-${bracketName}-${match.index}`;
              if (!seenVariableIds.has(variableId) && assignedTo === 'Client') {
                orderedVariables.push({
                  id: variableId,
                  type: variableType,
                  variableTitle: displayName,
                  content: `[${bracketName}]`,
                  assignedTo: assignedTo,
                  isDirect: false,
                  parentElementId: element.id,
                  parentSubItemId: subItem.id,
                  isBracketVariable: true,
                  bracketName: bracketName,
                  elementIndex: elementIndex,
                  matchIndex: match.index
                });
                seenVariableIds.add(variableId);
              }
            }
          }
        });
      }
    });
    
    // Sort by elementIndex and matchIndex to maintain document order
    orderedVariables.sort((a, b) => {
      if (a.elementIndex !== b.elementIndex) {
        return a.elementIndex - b.elementIndex;
      }
      return (a.matchIndex || 0) - (b.matchIndex || 0);
    });
    
    // Filter out duplicate variables - keep only the first occurrence
    const seenVariables = new Set();
    orderedVariables = orderedVariables.filter(variable => {
      if (variable.type === 'date') {
        if (seenVariables.has('date')) {
          return false;
        }
        seenVariables.add('date');
        return true;
      }
      
      if (variable.type === 'signature') {
        if (seenVariables.has('signature')) {
          return false;
        }
        seenVariables.add('signature');
        return true;
      }
      
      // Filter out duplicate checkboxes - keep only the first one
      if (variable.type === 'checkbox') {
        if (seenVariables.has('checkbox')) {
          return false; // Remove subsequent checkbox variables
        }
        seenVariables.add('checkbox');
        return true; // Keep the first checkbox variable
      }
      
      if (variable.type === 'initial' && variable.variableTitle) {
        const normalizedTitle = variable.variableTitle.toLowerCase().trim();
        const titleKey = `${variable.type}-${normalizedTitle}`;
        if (seenVariables.has(titleKey)) {
          return false;
        }
        seenVariables.add(titleKey);
        return true;
      }
      
      if (variable.isBracketVariable && variable.bracketName) {
        const key = `bracket-${variable.bracketName}`;
        if (seenVariables.has(key)) {
          return false;
        }
        seenVariables.add(key);
        return true;
      }
      
      if (variable.variableTitle) {
        const normalizedTitle = variable.variableTitle.toLowerCase().trim();
        const key = variable.originalContent 
          ? `${variable.type}-${variable.originalContent.toUpperCase()}` 
          : `${variable.type}-${normalizedTitle}`;
        if (seenVariables.has(key)) {
          return false;
        }
        seenVariables.add(key);
        return true;
      }
      
      return true;
    });
    
    // Add combined E-SIGN Act consent and agreement checkboxes as a single variable
    // Insert at position 19 (after routing number and checkbox), or at the end if there are fewer than 19 variables
    const insertIndex = orderedVariables.length >= 19 ? 19 : orderedVariables.length;
    
    orderedVariables.splice(insertIndex, 0, {
      id: 'e-sign-consent-and-agreement',
      type: 'combined_consent_checkbox',
      variableTitle: 'Electronic Signature Consent (E-SIGN Act Required)',
      content: '',
      assignedTo: 'Client',
      isDirect: true,
      isCombinedConsentCheckbox: true,
      elementIndex: 9999,
      matchIndex: 0
    });
    
    return orderedVariables;
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const inputValue = type === 'checkbox' ? checked : value;
    
    setFormData(prev => ({
      ...prev,
      [name]: inputValue
    }));
    
    // Clear error when user starts typing
    if (error) {
      setError('');
    }
  };

  const handleDynamicInputChange = (variableId, value) => {
    setDynamicFormData(prev => ({
      ...prev,
      [variableId]: value
    }));
  };

  // Get value for a variable - from dynamicFormData first, then fall back to prepopulated formData
  const getValueForVariable = (variable) => {
    const fromDynamic = dynamicFormData[variable?.id];
    if (fromDynamic !== undefined && fromDynamic !== null && (typeof fromDynamic !== 'string' || fromDynamic.trim())) {
      return fromDynamic;
    }
    const title = (variable?.variableTitle || '').toLowerCase();
    if (variable?.type === 'signature') return formData.signature || '';
    if (variable?.type === 'initial') return formData.initial || '';
    if (title.includes('company') && title.includes('name')) return formData.companyName || '';
    if (title.includes('company') && title.includes('address')) return formData.companyAddress || '';
    if (title.includes('signer') && title.includes('name')) return formData.signerName || '';
    if (title.includes('signer') && title.includes('title')) return formData.signerTitle || '';
    if (title.includes('city')) return formData.city || '';
    if (title.includes('state')) return formData.state || '';
    if (title.includes('zip')) return formData.zipCode || '';
    if (title.includes('business') && title.includes('structure')) return formData.businessStructure || '';
    if (title.includes('name') && !title.includes('company')) return formData.signerName || '';
    if (title.includes('address')) return formData.companyAddress || '';
    if (title.includes('title') || title.includes('position')) return formData.signerTitle || '';
    return '';
  };

  // Get variable type from bracket name
  const getVariableTypeFromBracket = (bracketName) => {
    const lowerName = bracketName.toLowerCase();
    if (lowerName.includes('signature') || lowerName.includes('sign')) return 'signature';
    if (lowerName.includes('initial')) return 'initial';
    if (lowerName.includes('date')) return 'date';
    if (lowerName.includes('check') || lowerName.includes('agree')) return 'checkbox';
    return 'text_input'; // Default to text input
  };

  // Clean placeholder text by removing brackets and underscores
  const cleanPlaceholderText = (content) => {
    if (!content) return '';
    
    // Remove brackets and clean up the text
    return content
      .replace(/\[([^\]]+)\]/g, '$1') // Remove brackets but keep content
      .replace(/_/g, ' ') // Replace underscores with spaces
      .replace(/\b\w/g, l => l.toUpperCase()); // Capitalize first letter of each word
  };

  // Convert bracket notation to user-friendly display placeholder
  const getUserFriendlyPlaceholder = (bracketNotation) => {
    if (!bracketNotation) return '';
    
    // Remove brackets
    let cleaned = bracketNotation.replace(/\[|\]/g, '');
    
    // Convert to readable format
    cleaned = cleaned
      .replace(/_/g, ' ') // Replace underscores with spaces
      .replace(/\b\w/g, l => l.toUpperCase()); // Capitalize first letter of each word
    
    // Create an underlined placeholder that looks like a form field
    const placeholderLength = Math.max(cleaned.length * 8, 120);
    return `<span class="inline-block border-b-2 border-dashed border-gray-400 dark:border-gray-500 px-1 text-gray-500 dark:text-gray-400 italic" style="min-width: ${placeholderLength}px;">${cleaned}</span>`;
  };

  // Extract variables from content
  const extractVariablesFromContent = (content) => {
    const variablePattern = /\[(SIGNATURE|INITIALS?|TEXT_INPUT|CHECKBOX|DATE)\]/g;
    const matches = content.match(variablePattern);
    return matches ? matches.map(match => {
      const varName = match.slice(1, -1).toLowerCase();
      return varName === 'initials' ? 'initial' : varName;
    }) : [];
  };

  // Get variable value - maps bracket names (Company_Name, State, etc.) to formData
  const getVariableValue = (variableName) => {
    // Check dynamic form data first (by variable id - not typically used here)
    if (dynamicFormData[variableName] !== undefined) {
      return dynamicFormData[variableName];
    }
    
    // Check global variables
    if (variableName === 'contract_number') {
      console.log('Getting contract number:', contractNumber);
      return contractNumber ? contractNumber.toString() : '';
    }
    
    // Map bracket names (Company_Name, State, Company_Address, etc.) to formData
    const bracketToFormData = {
      'companyname': formData.companyName,
      'company_name': formData.companyName,
      'state': formData.state,
      'companyaddress': formData.companyAddress,
      'company_address': formData.companyAddress,
      'city': formData.city,
      'zipcode': formData.zipCode,
      'zip_code': formData.zipCode,
      'businessstructure': formData.businessStructure,
      'business_structure': formData.businessStructure,
      'signername': formData.signerName,
      'signer_name': formData.signerName,
      'signertitle': formData.signerTitle,
      'signer_title': formData.signerTitle,
      'signature': formData.signature,
      'initial': formData.initial,
      'initials': formData.initial,
      'address': formData.companyAddress,
      'company': formData.companyName,
      'name': formData.signerName,
      'title': formData.signerTitle,
    };
    
    const variableNameLower = variableName.toLowerCase().replace(/\s+/g, '_');
    return bracketToFormData[variableNameLower] ?? bracketToFormData[variableName.toLowerCase()] ?? '';
  };

  const handleScroll = (e) => {
    const bottom = e.target.scrollHeight - e.target.scrollTop === e.target.clientHeight;
    if (bottom) {
      setIsScrolledToBottom(true);
    }
  };

  const handleConsentChange = (type) => {
    setConsentData(prev => ({
      ...prev,
      [type]: !prev[type],
      consentTimestamp: new Date().toISOString()
    }));
  };

  const handleCheckboxChange = () => {
    setHasAgreed(!hasAgreed);
  };

  // Get enhanced form data that includes both formData and dynamicFormData
  const getEnhancedFormData = () => {
    const enhancedFormData = { ...formData };
    const clientVariables = getClientVariables();
    
    console.log('getEnhancedFormData - clientVariables:', clientVariables);
    console.log('getEnhancedFormData - dynamicFormData:', dynamicFormData);
    console.log('getEnhancedFormData - initial formData:', formData);
    console.log('getEnhancedFormData - formData.initial:', formData.initial);
    console.log('getEnhancedFormData - enhancedFormData.initial before processing:', enhancedFormData.initial);
    
    // Map all dynamic form data to the appropriate legacy form fields
    clientVariables.forEach(variable => {
      const value = dynamicFormData[variable.id];
      if (value && (typeof value !== 'string' || value.trim())) {
        const title = variable.variableTitle.toLowerCase();
        
        console.log(`Processing variable: ${variable.variableTitle} (${variable.type}) = "${value}"`);
        
        // Map based on variable title content and type
        let mapped = false;
        
        // Check by type first (most reliable)
        if (variable.type === 'signature') {
          enhancedFormData.signature = value;
          console.log('Mapped to signature (by type):', value);
          mapped = true;
        } else if (variable.type === 'initial') {
          enhancedFormData.initial = value;
          console.log('Mapped to initial (by type):', value);
          mapped = true;
        }
        
        // Then check by title content
        if (!mapped) {
          if (title.includes('signer') && title.includes('name')) {
            enhancedFormData.signerName = value;
            console.log('Mapped to signerName:', value);
            mapped = true;
          } else if (title.includes('signer') && title.includes('title')) {
            enhancedFormData.signerTitle = value;
            console.log('Mapped to signerTitle:', value);
            mapped = true;
          } else if (title.includes('company') && title.includes('name')) {
            enhancedFormData.companyName = value;
            console.log('Mapped to companyName:', value);
            mapped = true;
          } else if (title.includes('company') && title.includes('address')) {
            enhancedFormData.companyAddress = value;
            console.log('Mapped to companyAddress:', value);
            mapped = true;
          } else if (title.includes('city')) {
            enhancedFormData.city = value;
            console.log('Mapped to city:', value);
            mapped = true;
          } else if (title.includes('state')) {
            enhancedFormData.state = value;
            console.log('Mapped to state:', value);
            mapped = true;
          } else if (title.includes('zip')) {
            enhancedFormData.zipCode = value;
            console.log('Mapped to zipCode:', value);
            mapped = true;
          } else if (title.includes('business') && title.includes('structure')) {
            enhancedFormData.businessStructure = value;
            console.log('Mapped to businessStructure:', value);
            mapped = true;
          }
        }
        
        // Additional fallback mappings for common patterns
        if (!mapped) {
          if (title.includes('name') && !title.includes('company')) {
            // If it's a name field but not company name, likely signer name
            enhancedFormData.signerName = value;
            console.log('Mapped to signerName (fallback):', value);
            mapped = true;
          } else if (title.includes('address')) {
            enhancedFormData.companyAddress = value;
            console.log('Mapped to companyAddress (fallback):', value);
            mapped = true;
          } else if (title.includes('title') || title.includes('position')) {
            enhancedFormData.signerTitle = value;
            console.log('Mapped to signerTitle (fallback):', value);
            mapped = true;
          }
        }
        
        if (!mapped) {
          console.log('No mapping found for variable:', variable.variableTitle, variable.type);
        }
      }
    });
    
    // Fallback to legacy form data for any fields that weren't found in dynamic data
    // This ensures backward compatibility
    if (!enhancedFormData.signerName && formData.signerName) {
      enhancedFormData.signerName = formData.signerName;
    }
    if (!enhancedFormData.signerTitle && formData.signerTitle) {
      enhancedFormData.signerTitle = formData.signerTitle;
    }
    if (!enhancedFormData.companyName && formData.companyName) {
      enhancedFormData.companyName = formData.companyName;
    }
    if (!enhancedFormData.companyAddress && formData.companyAddress) {
      enhancedFormData.companyAddress = formData.companyAddress;
    }
    if (!enhancedFormData.city && formData.city) {
      enhancedFormData.city = formData.city;
    }
    if (!enhancedFormData.state && formData.state) {
      enhancedFormData.state = formData.state;
    }
    if (!enhancedFormData.zipCode && formData.zipCode) {
      enhancedFormData.zipCode = formData.zipCode;
    }
    if (!enhancedFormData.businessStructure && formData.businessStructure) {
      enhancedFormData.businessStructure = formData.businessStructure;
    }
    if (!enhancedFormData.signature && formData.signature) {
      enhancedFormData.signature = formData.signature;
    }
    if (!enhancedFormData.initial && formData.initial) {
      enhancedFormData.initial = formData.initial;
    }
    
    console.log('getEnhancedFormData - final enhancedFormData:', enhancedFormData);
    
    return enhancedFormData;
  };

  const validateForm = () => {
    if (!consentData.consentToElectronic) {
      setError('You must consent to electronic signatures');
      return false;
    }
    
    if (!consentData.consentToRecords) {
      setError('You must consent to electronic records');
      return false;
    }
    
    if (!hasAgreed) {
      setError('You must agree to the terms of the contract');
      return false;
    }

    // E-SIGN Act compliance validation
    console.log('E-SIGN validation data:', {
      consentData,
      formData,
      dynamicFormData
    });
    
    // Create enhanced form data that includes dynamic form data
    const enhancedFormData = getEnhancedFormData();
    
    console.log('Enhanced form data for validation:', enhancedFormData);
    const eSignValidation = ESignatureService.validateESignCompliance(consentData, enhancedFormData);
    console.log('E-SIGN validation result:', eSignValidation);
    if (!eSignValidation.isValid) {
      setError(eSignValidation.errors.join(', '));
      return false;
    }
    
    setError('');
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      // Get the document content for tamper-proof snapshot
      const documentContent = document.querySelector('.overflow-y-auto').innerText;
      
      // Use E-SIGN Act compliant service to save the signature
      const enhancedFormData = getEnhancedFormData();
      const signedDocument = await ESignatureService.saveESignature(
        currentUser.uid,
        documentContent,
        { ...enhancedFormData, ...dynamicFormData, isContract: true, documentType: 'contract' },
        consentData
      );

      // Save contract to user and lead
      await saveContractToUserAndLead(signedDocument, { ...enhancedFormData, ...dynamicFormData });
      
      // Increment the contract number for next contract
      await incrementContractNumber();
      
      // Mark contract as signed (checkoutCompleted only after payment)
      if (currentUser) {
        try {
          await updateDoc(doc(db, 'users', currentUser.uid), {
            startFlowProgress: {
              completedSteps: [1, 2, 3, 4, 5],
              contractSigned: true,
              programData: {
                name: selectedProgram.name,
                startDate: checkoutData?.startDate
              },
              lastUpdated: new Date().toISOString()
            },
            lastUpdated: new Date().toISOString()
          });
          console.log('Contract signed - returning to checkout for payment');
        } catch (error) {
          console.error('Error updating after contract signing:', error);
        }
      }

      // Navigate back to Checkout step 3 (payment) - welcome message shows after payment
      const isPortal = location.pathname.startsWith('/portal');
      const checkoutPath = isPortal ? '/portal/checkout' : '/start/checkout';
      navigate(checkoutPath, {
        state: {
          contractSigned: true,
          contractData: {
            startDate: checkoutData?.startDate,
            contractLength: checkoutData?.contractLength
          },
          selectedProgram,
          selectedTier,
          billingCycle,
          customDeliverables: location.state?.customDeliverables,
          customDeliverableQuantities: location.state?.customDeliverableQuantities
        }
      });
    } catch (error) {
      console.error('Contract signing error:', error);
      setError('Contract signing failed. Please try again.');
    }
  };

  const saveContractToUserAndLead = async (signedDocument, formData) => {
    // Update user's onboarding progress if in onboarding mode
    if (isOnboardingMode) {
      await markStepComplete('contract_signed');
    }

    // Save audit trail data for viewing
    setAuditTrailData({
      documentType: 'Contract',
      signedAt: signedDocument.signedAt,
      signerInfo: {
        userId: currentUser.uid,
        email: currentUser.email,
        ipAddress: signedDocument.eSignCompliance?.auditTrail?.ipAddress || 'Unknown',
        userAgent: signedDocument.eSignCompliance?.auditTrail?.userAgent || navigator.userAgent,
        timestamp: signedDocument.signedAt
      },
      consentData: {
        consentToElectronic: signedDocument.eSignCompliance?.consentToElectronicSignatures || false,
        consentToRecords: signedDocument.eSignCompliance?.consentToElectronicRecords || false,
        consentTimestamp: signedDocument.eSignCompliance?.auditTrail?.consentTimestamp || new Date().toISOString()
      },
      auditTrail: {
        documentViewed: true,
        consentGiven: true,
        signatureProvided: true,
        documentCompleted: true
      }
    });

    // Save contract data to lead account
    try {
      console.log('Saving contract data to lead account for user:', currentUser.email);
      
      let accountsSnapshot = await getMyAccountSnapshot('Leads');
      
      if (!accountsSnapshot.empty) {
        const leadDoc = accountsSnapshot.docs[0];
        const leadData = leadDoc.data();
        
        console.log('Updating lead account:', leadDoc.id, 'with contract data');
        
        // Prepare contract data
        const contractData = {
          documentId: signedDocument.id,
          signerName: formData.signerName || '',
          signerTitle: formData.signerTitle || '',
          companyName: formData.companyName || '',
          companyAddress: formData.companyAddress || '',
          city: formData.city || '',
          state: formData.state || '',
          zipCode: formData.zipCode || '',
          businessStructure: formData.businessStructure || '',
          signature: formData.signature || '',
          initial: formData.initial || '',
          signedAt: signedDocument.signedAt || new Date().toISOString(),
          eSignCompliance: signedDocument.eSignCompliance || {},
          documentContent: signedDocument.eSignCompliance?.recordIntegrity?.documentSnapshot?.documentContent || 'Document content not available',
          // Include program and billing information
          program: selectedProgram?.name || '',
          programId: selectedProgram?.id || '',
          tier: selectedTier || '',
          billingCycle: billingCycle || '',
          startDate: checkoutData?.startDate || '',
          contractLength: checkoutData?.contractLength || '',
          // Include template elements for PDF generation
          templateElements: templateData?.contractElements || [],
          // Include banking information
          bankingInfo: {
            bankName: 'Chase',
            bankAddress: '45 wy st',
            accountNumber: '789856856',
            routingNumber: '965856856',
            nameOnAccount: formData.companyName || ''
          }
        };
        
        console.log('Contract data to save:', contractData);
        
        // Update the lead with contract information
        const currentDate = new Date().toISOString().split('T')[0];
        const substatusDates = leadData.substatusDates || {};
        substatusDates['Pending Payment'] = currentDate;
        
        // Add to statusHistory for tracking
        const statusHistory = leadData.statusHistory || [];
        const substatusEntry = {
          substatus: 'Pending Payment',
          date: new Date().toISOString(),
          updatedBy: 'System',
          action: 'Contract signed - substatus set to Pending Payment'
        };
        
        await updateDoc(doc(db, 'accounts', leadDoc.id), {
          contractStatus: 'Completed', // Lead has signed, but BBA signature still needed
          contractLastUpdated: new Date().toISOString(),
          signedContract: contractData,
          substatus: 'Pending Payment', // Update substatus when contract is signed
          substatusDates: substatusDates,
          statusHistory: [...statusHistory, substatusEntry],
          // Set ACH information from contract
          achInfo: contractData.bankingInfo,
          achStatus: 'Completed',
          achLastUpdated: new Date().toISOString(),
          lastUpdated: new Date().toISOString()
        });
        
        console.log('Contract data saved successfully to lead account');
      } else {
        console.log('No lead account found for user:', currentUser.email);
      }
    } catch (error) {
      console.error('Error saving contract to lead account:', error);
      throw error;
    }
  };

  const handleContinue = () => {
    navigate('/portal');
  };

  const handleViewAuditTrail = async () => {
    setShowAuditTrail(true);
  };

  // Helper function to get program items text from loaded contract items
  const getProgramItemsText = () => {
    console.log('getProgramItemsText called with contractItems:', contractItems);
    console.log('contractItems length:', contractItems?.length);
    
    if (!contractItems || contractItems.length === 0) {
      console.log('No contract items found, returning placeholder');
      return '[Program Items]';
    }
    
    // Convert contract items to readable text
    const itemsText = contractItems
      .map(item => {
        // Handle different possible field names for item name and quantity
        const name = item.name || item.itemName || item.title || 'Item';
        const quantity = item.quantity || item.qty || 1;
        
        // Handle checkbox mode (quantity '0') vs quantity mode
        if (quantity === '0' || quantity === 0) {
          return name; // Just show the item name for checkbox items
        } else {
          return `${name} (${quantity})`;
        }
      })
      .join(', ');
    
    console.log('Generated program items text:', itemsText);
    return itemsText;
  };

  // Helper function to get monthly price text from checkout data
  const getMonthlyPriceText = () => {
    if (!selectedProgram) {
      return '[Monthly Price]';
    }
    
    const basePrice = selectedProgram.pricePerMonth;
    let price = basePrice;
    let billingText = 'per month';
    
    // Apply billing cycle discounts if available
    if (billingCycle === 'quarterly') {
      price = Math.round(basePrice * 3 * 0.9); // 10% discount
      billingText = 'per quarter';
    } else if (billingCycle === 'annual') {
      price = Math.round(basePrice * 12 * 0.8); // 20% discount
      billingText = 'per year';
    }
    
    return `$${price} ${billingText}`;
  };

  // Helper function to get billing cycle text from checkout data
  const getBillingCycleText = () => {
    if (!billingCycle) {
      return '[Billing Cycle]';
    }
    
    // Convert billing cycle to proper case
    const cycleMap = {
      'monthly': 'Monthly',
      'quarterly': 'Quarterly', 
      'annual': 'Annual'
    };
    
    return cycleMap[billingCycle] || billingCycle;
  };

  // Helper function to get billing days text based on billing cycle
  const getBillingDaysText = () => {
    if (!billingCycle) {
      return '[Billing Days]';
    }
    
    // Calculate billing days based on billing cycle
    let days = 30; // Default for monthly
    
    if (billingCycle === 'quarterly') {
      days = 90; // 3 months * 30 days
    } else if (billingCycle === 'annual') {
      days = 365; // 1 year
    }
    
    return days.toString();
  };

  // Render content with variables replaced
  const renderContentWithVariables = (content, elementId = null) => {
    if (!content) return '';
    
    // First try to replace with dynamic form data
    let updatedContent = content;
    
    // Replace system variables first (these should be auto-populated, not client inputs)
    const systemVariables = {
      'Contract_Number': contractNumber ? contractNumber.toString() : '[Contract Number]',
      'Program_Name': selectedProgram ? selectedProgram.name : '[Program Name]',
      'Program_Items': getProgramItemsText(),
      'Monthly_Price': getMonthlyPriceText(),
      'Start_Date': checkoutData?.startDate ? new Date(checkoutData.startDate).toLocaleDateString() : new Date().toLocaleDateString(),
      'Billing_Cycle': getBillingCycleText(),
      'Billing_Days': getBillingDaysText()
    };
    
    // Replace system variables
    Object.entries(systemVariables).forEach(([variableName, value]) => {
      const systemPattern = new RegExp(`\\[${variableName}\\]`, 'g');
      updatedContent = updatedContent.replace(systemPattern, value);
    });
    
    // Replace special variables like [SIGNATURE], [INITIAL], [INITIALS], etc.
    // Use case-insensitive flag to match [Signature], [Date], etc.
    const specialPattern = /\[(SIGNATURE|INITIAL|INITIALS|TEXT_INPUT|CHECKBOX|DATE)\]/gi;
    updatedContent = updatedContent.replace(specialPattern, (match, variableType) => {
      let variableTypeLower = variableType.toLowerCase();
      // Handle INITIALS -> initial mapping
      if (variableTypeLower === 'initials') {
        variableTypeLower = 'initial';
      }
      // Find the first variable of this type in client variables
      const clientVariables = getClientVariables();
      
      // For INITIAL/INITIALS, we need to match by exact title to distinguish "Initial", "Initials", "Initials Two", etc.
      const originalMatch = variableType.toUpperCase(); // Preserve original to distinguish INITIAL vs INITIALS
      let matchingVariable = null;
      
      if (variableTypeLower === 'initial') {
        // For initial type, match by exact normalized title
        let expectedTitle = null;
        if (originalMatch === 'INITIALS') {
          expectedTitle = 'initials';
        } else if (originalMatch === 'INITIAL') {
          expectedTitle = 'initial';
        }
        
        // Priority 1: If there's a highlighted variable, check if it matches this type and exact title
        if (highlightedVariableId && expectedTitle) {
          const highlightedVar = variablesInOrder.find(v => 
            v.id === highlightedVariableId && 
            v.type === variableTypeLower &&
            v.variableTitle &&
            v.variableTitle.toLowerCase().trim() === expectedTitle
          );
          if (highlightedVar) {
            matchingVariable = highlightedVar;
          } else {
            // Also check by element and title
            const highlightedVarByElement = variablesInOrder.find(v => 
              v.id === highlightedVariableId && 
              v.type === variableTypeLower &&
              v.parentElementId === elementId &&
              v.variableTitle &&
              v.variableTitle.toLowerCase().trim() === expectedTitle
            );
            if (highlightedVarByElement) {
              matchingVariable = highlightedVarByElement;
            }
          }
        }
        
        // Priority 2: Try to find a variable that matches the current element and exact title
        if (!matchingVariable && expectedTitle) {
          matchingVariable = clientVariables.find(v => 
            v.type === variableTypeLower && 
            v.parentElementId === elementId &&
            v.variableTitle &&
            v.variableTitle.toLowerCase().trim() === expectedTitle
          );
        }
        
        // Priority 3: If no match for current element, find the first variable of this type with exact title
        if (!matchingVariable && expectedTitle) {
          const orderedMatch = variablesInOrder.find(v => 
            v.type === variableTypeLower &&
            v.variableTitle &&
            v.variableTitle.toLowerCase().trim() === expectedTitle
          );
          if (orderedMatch) {
            matchingVariable = orderedMatch;
          } else {
            matchingVariable = clientVariables.find(v => 
              v.type === variableTypeLower &&
              v.variableTitle &&
              v.variableTitle.toLowerCase().trim() === expectedTitle
            );
          }
        }
        
        // Priority 4: Fallback to originalContent matching (for backward compatibility)
        if (!matchingVariable) {
          const orderedMatch = variablesInOrder.find(v => 
            v.type === variableTypeLower &&
            (v.originalContent ? v.originalContent.toUpperCase() === originalMatch : true)
          );
          if (orderedMatch) {
            matchingVariable = orderedMatch;
          } else {
            matchingVariable = clientVariables.find(v => 
              v.type === variableTypeLower &&
              (v.originalContent ? v.originalContent.toUpperCase() === originalMatch : true)
            );
          }
        }
      } else {
        // For other types (signature, date, etc.), use original matching logic
        // Priority 1: If there's a highlighted variable, check if it matches this type AND element first
        if (highlightedVariableId) {
          // First try to find the highlighted variable that matches both type and element
          const highlightedVar = variablesInOrder.find(v => 
            v.id === highlightedVariableId && 
            v.type === variableTypeLower &&
            v.parentElementId === elementId
          );
          if (highlightedVar) {
            matchingVariable = highlightedVar;
          } else {
            // If not found by element, try just by ID and type (for cases where element might not match exactly)
            const highlightedVarById = variablesInOrder.find(v => 
              v.id === highlightedVariableId && 
              v.type === variableTypeLower
            );
            if (highlightedVarById) {
              matchingVariable = highlightedVarById;
            }
          }
        }
        
        // Priority 2: Try to find a variable that matches the current element
        if (!matchingVariable) {
          matchingVariable = clientVariables.find(v => 
            v.type === variableTypeLower && 
            v.parentElementId === elementId
          );
        }
        
        // Priority 3: If no match found for current element, find any variable of this type from variablesInOrder
        if (!matchingVariable) {
          matchingVariable = variablesInOrder.find(v => v.type === variableTypeLower);
        }
        
        // Priority 4: Fallback to clientVariables
        if (!matchingVariable) {
          matchingVariable = clientVariables.find(v => v.type === variableTypeLower);
        }
      }
      
      // Check if this is a BBA variable (should not be replaced)
      if (!matchingVariable) {
        const allVariables = getAllVariables();
        const bbaVariable = allVariables.find(v => 
          v.type === variableTypeLower && 
          v.assignedTo === 'BBA' &&
          v.parentElementId === elementId
        );
        if (bbaVariable) {
          return match; // Keep original placeholder for BBA variables
        }
      }
      
      // Debug logging for initials, signature, and date
      if (variableTypeLower === 'initial' || variableTypeLower === 'signature' || variableTypeLower === 'date') {
        const expectedTitle = variableTypeLower === 'initial' && originalMatch === 'INITIALS' ? 'initials' : (variableTypeLower === 'initial' && originalMatch === 'INITIAL' ? 'initial' : null);
        console.log(`Looking for ${variableTypeLower} variable:`, {
          elementId,
          originalVariableType: variableType,
          originalMatch,
          expectedTitle,
          variableTypeLower,
          highlightedVariableId,
          variablesInOrder: variablesInOrder.filter(v => v.type === variableTypeLower).map(v => ({ id: v.id, parentElementId: v.parentElementId, variableTitle: v.variableTitle, originalContent: v.originalContent, isBracketVariable: v.isBracketVariable, bracketName: v.bracketName })),
          clientVariables: clientVariables.filter(v => v.type === variableTypeLower).map(v => ({ id: v.id, parentElementId: v.parentElementId, variableTitle: v.variableTitle, isBracketVariable: v.isBracketVariable, bracketName: v.bracketName })),
          matchingVariable: matchingVariable ? { id: matchingVariable.id, parentElementId: matchingVariable.parentElementId, assignedTo: matchingVariable.assignedTo, variableTitle: matchingVariable.variableTitle, originalContent: matchingVariable.originalContent, isBracketVariable: matchingVariable.isBracketVariable, bracketName: matchingVariable.bracketName } : null,
          isHighlighted: matchingVariable ? highlightedVariableId === matchingVariable.id : false,
          hasValue: matchingVariable ? !!dynamicFormData[matchingVariable.id] : false,
          value: matchingVariable ? dynamicFormData[matchingVariable.id] : null,
        });
      }
      
      if (matchingVariable) {
        const isHighlighted = highlightedVariableId === matchingVariable.id;
        const highlightClass = isHighlighted 
          ? 'transition-all duration-300 bg-yellow-200 dark:bg-yellow-900/70 rounded px-1 border-2 border-yellow-400 dark:border-yellow-600' 
          : '';
        const highlightAttr = matchingVariable ? `data-variable-id="${matchingVariable.id}"` : '';
        
        // Only use dynamic form data for Client-assigned variables (assignedTo === 'Client' or null)
        const isClientVariable = matchingVariable.assignedTo === 'Client' || matchingVariable.assignedTo === null || !matchingVariable.assignedTo;
        
        // Check if the matching variable has a value, or if any instance of this variable has a value
        let value = null;
        if (isClientVariable) {
          // First check if the matched variable has a value
          if (dynamicFormData[matchingVariable.id]) {
            value = dynamicFormData[matchingVariable.id];
          } else if (variableTypeLower === 'initial' && matchingVariable.variableTitle) {
            // For initial type, check if any other instance with the same title has a value
            const expectedTitle = matchingVariable.variableTitle.toLowerCase().trim();
            const variableWithValue = clientVariables.find(v => 
              v.type === variableTypeLower && 
              v.variableTitle &&
              v.variableTitle.toLowerCase().trim() === expectedTitle &&
              dynamicFormData[v.id]
            ) || variablesInOrder.find(v => 
              v.type === variableTypeLower && 
              v.variableTitle &&
              v.variableTitle.toLowerCase().trim() === expectedTitle &&
              dynamicFormData[v.id]
            );
            if (variableWithValue) {
              value = dynamicFormData[variableWithValue.id];
              // Keep the original matchingVariable for highlighting, but use the value from variableWithValue
            }
          } else {
            // For other types, check if any other instance has a value
            const variableWithValue = clientVariables.find(v => 
              v.type === variableTypeLower && 
              dynamicFormData[v.id]
            ) || variablesInOrder.find(v => 
              v.type === variableTypeLower && 
              dynamicFormData[v.id]
            );
            if (variableWithValue) {
              value = dynamicFormData[variableWithValue.id];
            }
          }
        }
        
        if (isClientVariable && value) {
          // Apply cursive styling for signature text - only for Client-assigned variables
          if (variableTypeLower === 'signature') {
            return `<span ${highlightAttr} class="${highlightClass}" style="font-family: 'Dancing Script', 'Brush Script MT', cursive; font-size: 1.2em; font-weight: 600;">${value}</span>`;
          }
          // Format date value for display (convert YYYY-MM-DD to readable format)
          if (variableTypeLower === 'date' && value) {
            let formattedValue = value;
            try {
              // Parse YYYY-MM-DD format manually to avoid timezone issues
              if (typeof formattedValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(formattedValue)) {
                const [year, month, day] = formattedValue.split('-').map(Number);
                const dateObj = new Date(year, month - 1, day); // month is 0-indexed
                formattedValue = dateObj.toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                });
              } else {
                // Fallback for other date formats
                const dateObj = new Date(formattedValue);
                if (!isNaN(dateObj.getTime())) {
                  formattedValue = dateObj.toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  });
                }
              }
            } catch (e) {
              // If date parsing fails, use the value as-is
            }
            return `<span ${highlightAttr} class="${highlightClass}">${formattedValue}</span>`;
          }
          // Render checkbox as actual checkbox instead of text
          if (variableTypeLower === 'checkbox') {
            const isChecked = value === 'true' || value === true;
            return `<input type="checkbox" ${isChecked ? 'checked' : ''} disabled style="margin-right: 4px;" />`;
          }
          return `<span ${highlightAttr} class="${highlightClass}">${value}</span>`;
        }
        // For BBA-assigned variables, keep the original placeholder
        if (matchingVariable.assignedTo === 'BBA') {
          if (isHighlighted) {
            return `<span ${highlightAttr} class="${highlightClass}">${match}</span>`;
          }
          return match; // Keep original placeholder for BBA variables
        }
        
        // If no value, always add data-variable-id so scroll can find it, and highlight if needed
        // This applies to both Client and null-assigned variables
        if (!value && isClientVariable) {
          const userFriendlyPlaceholder = getUserFriendlyPlaceholder(match);
          return `<span ${highlightAttr} class="${highlightClass}">${userFriendlyPlaceholder}</span>`;
        }
      }
      
      // Fall back to legacy formData for initials
      if (variableTypeLower === 'initial' && formData.initial) {
        const matchingInitial = clientVariables.find(v => 
          v.type === 'initial' && v.parentElementId === elementId
        ) || variablesInOrder.find(v => v.type === 'initial');
        const isHighlighted = matchingInitial && highlightedVariableId === matchingInitial.id;
        const highlightClass = isHighlighted 
          ? 'transition-all duration-300 bg-yellow-200 dark:bg-yellow-900/70 rounded px-1 border-2 border-yellow-400 dark:border-yellow-600' 
          : '';
        const highlightAttr = matchingInitial ? `data-variable-id="${matchingInitial.id}"` : '';
        return `<span ${highlightAttr} class="${highlightClass}">${formData.initial}</span>`;
      }
      
      // Fall back to legacy formData for signature - only apply cursive if it's a Client variable
      if (variableTypeLower === 'signature' && formData.signature) {
        // Check if this is a Client-assigned variable by looking at the matching variable
        const clientVariables = getClientVariables();
        const matchingSignature = clientVariables.find(v => 
          v.type === 'signature' && v.assignedTo === 'Client' && v.parentElementId === elementId
        ) || variablesInOrder.find(v => v.type === 'signature' && v.assignedTo === 'Client');
        const isHighlighted = matchingSignature && highlightedVariableId === matchingSignature.id;
        const highlightClass = isHighlighted 
          ? 'transition-all duration-300 bg-yellow-200 dark:bg-yellow-900/70 rounded px-1 border-2 border-yellow-400 dark:border-yellow-600' 
          : '';
        const highlightAttr = matchingSignature ? `data-variable-id="${matchingSignature.id}"` : '';
        if (matchingSignature) {
          return `<span ${highlightAttr} class="${highlightClass}" style="font-family: 'Dancing Script', 'Brush Script MT', cursive; font-size: 1.2em; font-weight: 600;">${formData.signature}</span>`;
        }
        return `<span ${highlightAttr} class="${highlightClass}">${formData.signature}</span>`;
      }
      
      // If no value found and no matching variable, return the original placeholder
      // But if we have a matching variable, still add data-variable-id for scrolling
      if (matchingVariable) {
        const userFriendlyPlaceholder = getUserFriendlyPlaceholder(match);
        return `<span data-variable-id="${matchingVariable.id}">${userFriendlyPlaceholder}</span>`;
      }
      
      return match; // Keep original if no value found and no matching variable
    });
    
    // Replace bracket variables like [Company_Name] - but skip system variables as they're already handled
    const bracketPattern = /\[([A-Za-z_][A-Za-z0-9_]*)\]/g;
    updatedContent = updatedContent.replace(bracketPattern, (match, variableName) => {
      // Skip system variables as they're already handled above
      if (['Contract_Number', 'Program_Name', 'Program_Items', 'Monthly_Price', 'Start_Date', 'Billing_Cycle', 'Billing_Days'].includes(variableName)) {
        return match; // This should have been replaced above, but just in case
      }
      
      // First try to find in client variables
      const clientVariables = getClientVariables();
      
      // Priority 1: If there's a highlighted variable, check if it matches this bracket name first
      // Use case-insensitive matching to handle [Signature]/[SIGNATURE], [Date]/[DATE], etc.
      let matchingVariable = null;
      const variableNameLower = variableName.toLowerCase();
      if (highlightedVariableId) {
        const highlightedVar = variablesInOrder.find(v => 
          v.id === highlightedVariableId && 
          v.isBracketVariable && 
          v.bracketName && 
          v.bracketName.toLowerCase() === variableNameLower
        );
        if (highlightedVar) {
          matchingVariable = highlightedVar;
        }
      }
      
      // Priority 2: Try to find by parentElementId (most accurate match for this specific instance)
      if (!matchingVariable) {
        matchingVariable = clientVariables.find(v => 
          v.isBracketVariable && 
          v.bracketName && 
          v.bracketName.toLowerCase() === variableNameLower && 
          v.parentElementId === elementId
        );
      }
      
      // Priority 3: If not found by parentElementId, try to find any variable with this bracket name
      if (!matchingVariable) {
        matchingVariable = variablesInOrder.find(v => 
          v.isBracketVariable && 
          v.bracketName && 
          v.bracketName.toLowerCase() === variableNameLower
        );
        
        if (!matchingVariable) {
          matchingVariable = clientVariables.find(v => 
            v.isBracketVariable && 
            v.bracketName && 
            v.bracketName.toLowerCase() === variableNameLower
          );
        }
      }
      
      // Check if the matching variable has a value, or if any instance of this variable has a value
      let value = null;
      if (matchingVariable) {
        // First check if the matched variable has a value
        if (dynamicFormData[matchingVariable.id]) {
          value = dynamicFormData[matchingVariable.id];
        } else {
          // If not, check if any other instance of this variable has a value
          // Use case-insensitive matching
          const variableWithValue = clientVariables.find(v => 
            v.isBracketVariable && 
            v.bracketName && 
            v.bracketName.toLowerCase() === variableNameLower && 
            dynamicFormData[v.id]
          ) || variablesInOrder.find(v => 
            v.isBracketVariable && 
            v.bracketName && 
            v.bracketName.toLowerCase() === variableNameLower && 
            dynamicFormData[v.id]
          );
          if (variableWithValue) {
            value = dynamicFormData[variableWithValue.id];
            // Use the variable with the value for highlighting purposes
            matchingVariable = variableWithValue;
          }
        }
        // Fall back to prepopulated formData when dynamicFormData has no value
        if (!value && (matchingVariable.assignedTo === 'Client' || matchingVariable.assignedTo === null || !matchingVariable.assignedTo)) {
          const prepopulatedValue = getValueForVariable(matchingVariable);
          if (prepopulatedValue) value = prepopulatedValue;
        }
      }
      
      const isHighlighted = matchingVariable && highlightedVariableId === matchingVariable.id;
      const highlightClass = isHighlighted 
        ? 'transition-all duration-300 bg-yellow-200 dark:bg-yellow-900/70 rounded px-1 border-2 border-yellow-400 dark:border-yellow-600' 
        : '';
      const highlightAttr = matchingVariable ? `data-variable-id="${matchingVariable.id}"` : '';
      
      // Check if this is a Client variable (assignedTo === 'Client' or null)
      const isClientVariable = matchingVariable && (matchingVariable.assignedTo === 'Client' || matchingVariable.assignedTo === null || !matchingVariable.assignedTo);
      
      // Debug logging for Initials, Signature, and Date bracket variables
      if (variableName === 'Initials' || variableName.toLowerCase().includes('initial') || 
          variableName === 'Signature' || variableName.toLowerCase().includes('signature') ||
          variableName === 'Date' || variableName.toLowerCase() === 'date') {
        console.log(`Bracket variable matching for ${variableName}:`, {
          variableName,
          elementId,
          highlightedVariableId,
          matchingVariable: matchingVariable ? { 
            id: matchingVariable.id, 
            bracketName: matchingVariable.bracketName, 
            parentElementId: matchingVariable.parentElementId,
            assignedTo: matchingVariable.assignedTo,
            variableTitle: matchingVariable.variableTitle,
            type: matchingVariable.type
          } : null,
          isHighlighted,
          isClientVariable,
          hasValue: !!value,
          value,
          variablesInOrder: variablesInOrder.filter(v => {
            if (variableName.toLowerCase().includes('initial')) {
              return v.isBracketVariable && (v.bracketName === 'Initials' || v.bracketName?.toLowerCase().includes('initial'));
            } else if (variableName.toLowerCase().includes('signature')) {
              return v.isBracketVariable && (v.bracketName === 'Signature' || v.bracketName?.toLowerCase().includes('signature'));
            } else if (variableName.toLowerCase() === 'date') {
              return v.isBracketVariable && (v.bracketName === 'Date' || v.bracketName?.toLowerCase() === 'date');
            }
            return false;
          }).map(v => ({ id: v.id, bracketName: v.bracketName, variableTitle: v.variableTitle, type: v.type }))
        });
      }
      
      if (matchingVariable && value && isClientVariable) {
        // Render checkbox as actual checkbox instead of text
        if (matchingVariable.type === 'checkbox') {
          const isChecked = value === 'true' || value === true;
          return `<input type="checkbox" ${isChecked ? 'checked' : ''} disabled style="margin-right: 4px;" />`;
        }
        // Format date value for display (convert YYYY-MM-DD to readable format)
        if (matchingVariable.type === 'date' && value) {
          let formattedValue = value;
          try {
            // Parse YYYY-MM-DD format manually to avoid timezone issues
            if (typeof formattedValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(formattedValue)) {
              const [year, month, day] = formattedValue.split('-').map(Number);
              const dateObj = new Date(year, month - 1, day); // month is 0-indexed
              formattedValue = dateObj.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              });
            } else {
              // Fallback for other date formats
              const dateObj = new Date(formattedValue);
              if (!isNaN(dateObj.getTime())) {
                formattedValue = dateObj.toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                });
              }
            }
          } catch (e) {
            // If date parsing fails, use the value as-is
          }
          return `<span ${highlightAttr} class="${highlightClass}">${formattedValue}</span>`;
        }
        // Apply cursive styling for signature text (case-insensitive)
        if (variableNameLower.includes('signature') || variableNameLower.includes('sign')) {
          return `<span ${highlightAttr} class="${highlightClass}" style="font-family: 'Dancing Script', 'Brush Script MT', cursive; font-size: 1.2em; font-weight: 600;">${value}</span>`;
        }
        return `<span ${highlightAttr} class="${highlightClass}">${value}</span>`;
      }
      
      // If no value but matching variable exists, always add data-variable-id and show placeholder
      // This ensures scrolling works even when not highlighted
      if (matchingVariable && !value && isClientVariable) {
        const userFriendlyPlaceholder = getUserFriendlyPlaceholder(match);
        // Always include highlight attributes so scroll can find it
        return `<span ${highlightAttr} class="${highlightClass}">${userFriendlyPlaceholder}</span>`;
      }
      
      // Check if this is a BBA variable (should not be replaced)
      const allVariables = getAllVariables();
      const bbaVariable = allVariables.find(v => 
        v.isBracketVariable && 
        v.bracketName && 
        v.bracketName.toLowerCase() === variableNameLower && 
        v.assignedTo === 'BBA' &&
        v.parentElementId === elementId
      );
      if (bbaVariable) {
        return match; // Keep original placeholder for BBA variables
      }
      
      // Fall back to legacy form data for Client variables only
      // Check if this is a Client variable (either by matchingVariable or by checking clientVariables)
      const isClientVariableLegacy = !matchingVariable && clientVariables.some(v => 
        v.isBracketVariable && 
        v.bracketName && 
        v.bracketName.toLowerCase() === variableNameLower && 
        v.parentElementId === elementId
      );
      
      if (isClientVariableLegacy) {
        // Find the matching variable for highlighting
        const legacyMatchingVariable = clientVariables.find(v => 
          v.isBracketVariable && 
          v.bracketName && 
          v.bracketName.toLowerCase() === variableNameLower && 
          v.parentElementId === elementId
        ) || variablesInOrder.find(v => 
          v.isBracketVariable && 
          v.bracketName && 
          v.bracketName.toLowerCase() === variableNameLower
        );
        const legacyIsHighlighted = legacyMatchingVariable && highlightedVariableId === legacyMatchingVariable.id;
        const legacyHighlightClass = legacyIsHighlighted 
          ? 'transition-all duration-300 bg-yellow-200 dark:bg-yellow-900/70 rounded px-1 border-2 border-yellow-400 dark:border-yellow-600' 
          : '';
        const legacyHighlightAttr = legacyMatchingVariable ? `data-variable-id="${legacyMatchingVariable.id}"` : '';
        
        const legacyValue = getVariableValue(variableName);
        if (legacyValue !== `[${variableName}]`) {
          // Apply cursive styling for signature text - only for Client-assigned variables
          if (variableNameLower.includes('signature') || variableNameLower.includes('sign')) {
            return `<span ${legacyHighlightAttr} class="${legacyHighlightClass}" style="font-family: 'Dancing Script', 'Brush Script MT', cursive; font-size: 1.2em; font-weight: 600;">${legacyValue}</span>`;
          }
          // Render checkbox as actual checkbox instead of text
          if (variableNameLower.includes('check') || variableNameLower.includes('agree')) {
            const isChecked = legacyValue === 'true' || legacyValue === true;
            return `<input type="checkbox" ${isChecked ? 'checked' : ''} disabled style="margin-right: 4px;" />`;
          }
          return `<span ${legacyHighlightAttr} class="${legacyHighlightClass}">${legacyValue}</span>`;
        }
        // If no value but should be highlighted, highlight the placeholder
        if (legacyIsHighlighted) {
          const userFriendlyPlaceholder = getUserFriendlyPlaceholder(match);
          return `<span ${legacyHighlightAttr} class="${legacyHighlightClass}">${userFriendlyPlaceholder}</span>`;
        }
        // Always show placeholder with data-variable-id if we have a matching variable
        if (legacyMatchingVariable) {
          const userFriendlyPlaceholder = getUserFriendlyPlaceholder(match);
          return `<span ${legacyHighlightAttr} class="${legacyHighlightClass}">${userFriendlyPlaceholder}</span>`;
        }
      }
      
      // For BBA-assigned variables or unassigned variables, keep the original bracket notation
      return match;
    });
    
    return updatedContent;
  };

  // Get all variables from all elements
  const getAllVariables = () => {
    const variables = [];
    
    if (!templateData || !templateData.contractElements) {
      return variables;
    }
    
    templateData.contractElements.forEach(element => {
      // Direct input field types
      if (['signature', 'initial', 'text_input', 'checkbox', 'date'].includes(element.type)) {
        variables.push({
          id: element.id,
          type: element.type,
          content: element.content,
          assignedTo: element.assignedTo || 'Client',
          isDirect: true,
          variableTitle: element.variableTitle
        });
      }
      
      // Variables embedded in text content
      if (element.type === 'text') {
        const embeddedVariables = extractVariablesFromContent(element.content);
        embeddedVariables.forEach((varType, index) => {
          variables.push({
            id: `${element.id}-embedded-${index}`,
            type: varType,
            content: `[${varType.toUpperCase()}]`,
            assignedTo: element.assignedTo || 'Client', // Default for embedded variables
            isDirect: false,
            parentElementId: element.id
          });
        });

        // Bracket variables (e.g., [Company_Name])
        const bracketPattern = /\[([A-Za-z_][A-Za-z0-9_]*)\]/g;
        let match;
        while ((match = bracketPattern.exec(element.content)) !== null) {
          const bracketName = match[1];
          const variableType = getVariableTypeFromBracket(bracketName);
          const displayName = bracketName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
          
          variables.push({
            id: `${element.id}-bracket-${bracketName}-${match.index}`,
            type: variableType,
            content: `[${bracketName}]`,
            assignedTo: element.assignedTo || 'Client', // Use element's assigned to value
            isDirect: false,
            parentElementId: element.id,
            variableTitle: displayName,
            isBracketVariable: true,
            bracketName: bracketName
          });
        }
      }
    });
    
    return variables;
  };

  // Group client variables by their normalized names to detect duplicates
  const getGroupedClientVariables = () => {
    const clientVariables = getClientVariables();
    const grouped = {};
    
    console.log('Grouping client variables:', clientVariables);
    
    clientVariables.forEach(variable => {
      // Create a normalized key for grouping (case-insensitive, trimmed) + assignedTo to prevent mixing Client/BBA
      const normalizedKey = `${variable.variableTitle.toLowerCase().trim()}-${variable.assignedTo}`;
      
      console.log(`Variable "${variable.variableTitle}" (${variable.type}) assigned to ${variable.assignedTo} -> key: ${normalizedKey}`);
      
      if (!grouped[normalizedKey]) {
        grouped[normalizedKey] = {
          key: normalizedKey,
          variables: [],
          primaryVariable: variable
        };
      }
      
      grouped[normalizedKey].variables.push(variable);
    });
    
    const result = Object.values(grouped);
    console.log('Grouped result:', result);
    return result;
  };

  // Handle next variable
  const handleNext = () => {
    if (currentInputIndex < variablesInOrder.length - 1) {
      setCurrentInputIndex(currentInputIndex + 1);
    }
  };

  // Handle previous variable
  const handlePrevious = () => {
    if (currentInputIndex > 0) {
      setCurrentInputIndex(currentInputIndex - 1);
    }
  };

  // Render current input field (single field at a time)
  const renderCurrentInputField = () => {
    if (variablesInOrder.length === 0 || currentInputIndex >= variablesInOrder.length) {
      return null;
    }

    const currentVariable = variablesInOrder[currentInputIndex];
    const fieldId = `client-field-${currentVariable.id}`;
    
    // Get current value based on variable type (fall back to prepopulated formData for known variables)
    let currentValue = '';
    if (currentVariable.isConsentCheckbox && currentVariable.consentType) {
      currentValue = consentData[currentVariable.consentType] ? 'true' : 'false';
    } else if (currentVariable.isAgreementCheckbox) {
      currentValue = hasAgreed ? 'true' : 'false';
    } else {
      currentValue = getValueForVariable(currentVariable);
    }
    
    const handleChange = (value) => {
      // Handle consent checkboxes
      if (currentVariable.isConsentCheckbox && currentVariable.consentType) {
        setConsentData(prev => ({
          ...prev,
          [currentVariable.consentType]: value === 'true' || value === true,
          consentTimestamp: new Date().toISOString()
        }));
        setDynamicFormData(prev => ({ ...prev, [currentVariable.id]: value }));
        return;
      }
      
      // Handle agreement checkbox
      if (currentVariable.isAgreementCheckbox) {
        setHasAgreed(value === 'true' || value === true);
        setDynamicFormData(prev => ({ ...prev, [currentVariable.id]: value }));
        return;
      }
      
      // Update all variables in the group if this is part of a group
      const groupedVariables = getGroupedClientVariables();
      const group = groupedVariables.find(g => 
        g.variables.some(v => v.id === currentVariable.id)
      );
      
      const updates = {};
      if (group) {
        group.variables.forEach(variable => {
          updates[variable.id] = value;
        });
      } else {
        updates[currentVariable.id] = value;
      }
      
      setDynamicFormData(prev => ({ ...prev, ...updates }));
      
      // Also update legacy formData
      const title = currentVariable.variableTitle.toLowerCase();
      if (currentVariable.type === 'initial') {
        setFormData(prev => ({ ...prev, initial: value }));
      } else if (currentVariable.type === 'signature') {
        setFormData(prev => ({ ...prev, signature: value }));
      } else if (title.includes('signer') && title.includes('name')) {
        setFormData(prev => ({ ...prev, signerName: value }));
      } else if (title === 'title' || (title.includes('signer') && title.includes('title')) || title.includes('position')) {
        setFormData(prev => ({ ...prev, signerTitle: value }));
      } else if (title.includes('company') && title.includes('name')) {
        setFormData(prev => ({ ...prev, companyName: value }));
      } else if (title === 'address' || title.includes('address')) {
        setFormData(prev => ({ ...prev, companyAddress: value }));
      } else if (title.includes('city')) {
        setFormData(prev => ({ ...prev, city: value }));
      } else if (title.includes('state')) {
        setFormData(prev => ({ ...prev, state: value }));
      } else if (title.includes('zip')) {
        setFormData(prev => ({ ...prev, zipCode: value }));
      } else if (title.includes('business') && title.includes('structure')) {
        setFormData(prev => ({ ...prev, businessStructure: value }));
      } else if (title.includes('name') && !title.includes('company')) {
        setFormData(prev => ({ ...prev, signerName: value }));
      } else if (title.includes('title') || title.includes('position')) {
        setFormData(prev => ({ ...prev, signerTitle: value }));
      }
    };

    // Check if current input has a value
    const hasValue = () => {
      if (currentVariable.isConsentCheckbox || currentVariable.isAgreementCheckbox) {
        if (currentVariable.isConsentCheckbox && currentVariable.consentType) {
          return consentData[currentVariable.consentType];
        }
        if (currentVariable.isAgreementCheckbox) {
          return hasAgreed;
        }
      }
      return currentValue && (typeof currentValue !== 'string' || currentValue.trim());
    };

    const handleKeyDown = (e) => {
      if (e.key === 'Enter' && hasValue()) {
        e.preventDefault();
        handleNext();
      }
    };

    // Special rendering for combined consent checkboxes
    if (currentVariable.isCombinedConsentCheckbox) {
      const hasAllConsents = consentData.consentToElectronic && consentData.consentToRecords && hasAgreed;
      
      return (
        <div className="mb-6 p-4 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm">
          <div className="mb-4">
            <div className="flex items-center mb-3">
              <FileText className={`h-5 w-5 mr-2 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
              <h3 className={`text-sm font-medium ${darkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                Electronic Signature Consent (E-SIGN Act Required)
              </h3>
            </div>
          </div>
          
          <div className="mb-2">
            <span className="text-xs text-gray-500 dark:text-gray-400">
              ({currentInputIndex + 1} of {variablesInOrder.length})
            </span>
          </div>
          
          <div className="space-y-4">
            {/* Consent to electronic signatures checkbox */}
            <div className="flex items-start">
              <div className="flex items-center h-5">
                <input
                  id={`${fieldId}-electronic`}
                  type="checkbox"
                  checked={consentData.consentToElectronic || false}
                  onChange={(e) => {
                    setConsentData(prev => ({
                      ...prev,
                      consentToElectronic: e.target.checked,
                      consentTimestamp: new Date().toISOString()
                    }));
                  }}
                  className={`h-4 w-4 focus:ring-blue-500 border rounded cursor-pointer transition-colors duration-200 ${
                    darkMode 
                      ? 'text-blue-600 border-gray-600 bg-gray-800' 
                      : 'text-blue-600 border-gray-300'
                  }`}
                />
              </div>
              <div className="ml-3 text-sm">
                <label htmlFor={`${fieldId}-electronic`} className={`font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  I agree to use electronic signatures and electronic records instead of paper documents*
                </label>
              </div>
            </div>
            
            {/* Consent to electronic records checkbox */}
            <div className="flex items-start">
              <div className="flex items-center h-5">
                <input
                  id={`${fieldId}-records`}
                  type="checkbox"
                  checked={consentData.consentToRecords || false}
                  onChange={(e) => {
                    setConsentData(prev => ({
                      ...prev,
                      consentToRecords: e.target.checked,
                      consentTimestamp: new Date().toISOString()
                    }));
                  }}
                  className={`h-4 w-4 focus:ring-blue-500 border rounded cursor-pointer transition-colors duration-200 ${
                    darkMode 
                      ? 'text-blue-600 border-gray-600 bg-gray-800' 
                      : 'text-blue-600 border-gray-300'
                  }`}
                />
              </div>
              <div className="ml-3 text-sm">
                <label htmlFor={`${fieldId}-records`} className={`font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  I agree to receive and store electronic records of this agreement*
                </label>
              </div>
            </div>
            
            {/* Agreement checkbox */}
            <div className="flex items-start">
              <div className="flex items-center h-5">
                <input
                  id={`${fieldId}-agreement`}
                  type="checkbox"
                  checked={hasAgreed || false}
                  onChange={(e) => {
                    setHasAgreed(e.target.checked);
                  }}
                  className={`h-4 w-4 focus:ring-blue-500 border rounded cursor-pointer transition-colors duration-200 ${
                    darkMode 
                      ? 'text-blue-600 border-gray-600 bg-gray-800' 
                      : 'text-blue-600 border-gray-300'
                  }`}
                />
              </div>
              <div className="ml-3 text-sm">
                <label htmlFor={`${fieldId}-agreement`} className={`font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  I have read and agree to the terms of the Service Agreement*
                </label>
              </div>
            </div>
          </div>
          
          <div className="mt-3 text-xs text-gray-500 dark:text-gray-400">
            <p>By checking these boxes, you consent to conduct business electronically under the E-SIGN Act. 
            You may withdraw this consent at any time by contacting us in writing.</p>
          </div>
          
          <div className="flex justify-between mt-4">
            <button
              type="button"
              onClick={handlePrevious}
              disabled={currentInputIndex === 0}
              className={`px-4 py-2 rounded-md font-medium ${
                currentInputIndex === 0
                  ? 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                  : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
              }`}
            >
              Previous
            </button>
            {currentInputIndex >= variablesInOrder.length - 1 ? (
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  handleSubmit(e);
                }}
                disabled={!hasAllConsents || !isScrolledToBottom}
                className={`px-4 py-2 rounded-md font-medium flex items-center ${
                  !hasAllConsents || !isScrolledToBottom
                    ? 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                    : 'bg-red-600 text-white hover:bg-red-700'
                }`}
              >
                <Shield className="h-5 w-5 mr-2" />
                Sign & Agree (E-SIGN Act Compliant)
              </button>
            ) : (
              <button
                type="button"
                onClick={handleNext}
                disabled={!hasAllConsents}
                className={`px-4 py-2 rounded-md font-medium ${
                  !hasAllConsents
                    ? 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                    : 'bg-red-600 text-white hover:bg-red-700'
                }`}
              >
                Next
              </button>
            )}
          </div>
        </div>
      );
    }
    
    return (
      <div className="mb-6 p-4 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm">
        <div className="mb-2">
          <label htmlFor={fieldId} className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            {currentVariable.variableTitle}*
            <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
              ({currentInputIndex + 1} of {variablesInOrder.length})
            </span>
          </label>
        </div>
        
        {currentVariable.type === 'checkbox' ? (
          <div className="flex items-center">
            <input
              id={fieldId}
              type="checkbox"
              checked={currentValue === 'true' || currentValue === true}
              onChange={(e) => {
                handleChange(e.target.checked ? 'true' : 'false');
              }}
              className="mr-2"
            />
          </div>
        ) : currentVariable.variableTitle.toLowerCase().includes('business structure') || 
          currentVariable.variableTitle.toLowerCase().includes('structure') ? (
          <select
            id={fieldId}
            value={currentValue}
            onChange={(e) => {
              handleChange(e.target.value);
            }}
            onKeyDown={handleKeyDown}
            className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              darkMode 
                ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400' 
                : 'border-gray-300 text-gray-900 placeholder-gray-500'
            }`}
          >
            <option value="">Select Business Structure</option>
            <option value="corporation">Corporation</option>
            <option value="limited liability company">Limited Liability Company</option>
            <option value="partnership">Partnership</option>
            <option value="sole proprietorship">Sole Proprietorship</option>
            <option value="limited partnership">Limited Partnership</option>
          </select>
        ) : currentVariable.type === 'date' ? (
          <div className="flex gap-2">
            <input
              id={fieldId}
              type="date"
              value={currentValue}
              onChange={(e) => {
                handleChange(e.target.value);
              }}
              onKeyDown={handleKeyDown}
              className={`flex-1 px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                darkMode 
                  ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400' 
                  : 'border-gray-300 text-gray-900 placeholder-gray-500'
              }`}
              autoFocus
            />
            <button
              type="button"
              onClick={() => {
                const today = new Date();
                const year = today.getFullYear();
                const month = String(today.getMonth() + 1).padStart(2, '0');
                const day = String(today.getDate()).padStart(2, '0');
                const todayString = `${year}-${month}-${day}`;
                handleChange(todayString);
                setTimeout(() => {
                  if (currentInputIndex < variablesInOrder.length - 1) {
                    handleNext();
                  }
                }, 100);
              }}
              className={`px-4 py-2 rounded-md font-medium text-sm whitespace-nowrap ${
                darkMode
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              Today
            </button>
          </div>
        ) : currentVariable.type === 'signature' ? (
          <input
            id={fieldId}
            type="text"
            value={currentValue}
            onChange={(e) => {
              handleChange(e.target.value);
            }}
            onKeyDown={handleKeyDown}
            className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              darkMode 
                ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400' 
                : 'border-gray-300 text-gray-900 placeholder-gray-500'
            }`}
            placeholder="Signature"
            autoFocus
          />
        ) : currentVariable.type === 'initial' ? (
          <input
            id={fieldId}
            type="text"
            value={currentValue}
            onChange={(e) => {
              handleChange(e.target.value);
            }}
            onKeyDown={handleKeyDown}
            className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              darkMode 
                ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400' 
                : 'border-gray-300 text-gray-900 placeholder-gray-500'
            }`}
            placeholder="Initials"
            maxLength="3"
            autoFocus
          />
        ) : (
          <input
            id={fieldId}
            type="text"
            value={currentValue}
            onChange={(e) => {
              handleChange(e.target.value);
            }}
            onKeyDown={handleKeyDown}
            placeholder={cleanPlaceholderText(currentVariable.content)}
            className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              darkMode 
                ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400' 
                : 'border-gray-300 text-gray-900 placeholder-gray-500'
            }`}
            autoFocus
          />
        )}
        
        <div className="flex justify-between mt-4">
          <button
            type="button"
            onClick={handlePrevious}
            disabled={currentInputIndex === 0}
            className={`px-4 py-2 rounded-md font-medium ${
              currentInputIndex === 0
                ? 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
            }`}
          >
            Previous
          </button>
          <button
            type="button"
            onClick={handleNext}
            disabled={currentInputIndex >= variablesInOrder.length - 1 || !hasValue()}
            className={`px-4 py-2 rounded-md font-medium ${
              currentInputIndex >= variablesInOrder.length - 1 || !hasValue()
                ? 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                : 'bg-red-600 text-white hover:bg-red-700'
            }`}
          >
            Next
          </button>
        </div>
      </div>
    );
  };

  // Render grouped client input field
  const renderGroupedClientInputField = (group) => {
    const { primaryVariable, variables } = group;
    const fieldId = `client-field-${primaryVariable.id}`;
    
    // Debug logging for initials and signatures
    if (primaryVariable.type === 'initial' || primaryVariable.type === 'signature') {
      console.log(`Rendering ${primaryVariable.type} field:`, {
        primaryVariable,
        variables,
        dynamicFormData,
        currentValue: dynamicFormData[primaryVariable.id]
      });
    }
    
    // Use the value from dynamicFormData (any variable in group) or fall back to prepopulated formData
    const fromDynamic = dynamicFormData[primaryVariable.id] || variables.map(v => dynamicFormData[v.id]).find(v => v);
    const currentValue = fromDynamic || getValueForVariable(primaryVariable);
    
    // Update all variables in the group when the input changes
    const handleChange = (value) => {
      const updates = {};
      variables.forEach(variable => {
        updates[variable.id] = value;
      });
      
      // Debug logging for all field updates
      console.log(`Updating ${primaryVariable.type} field (${primaryVariable.variableTitle}):`, {
        value,
        updates,
        variables,
        variableAssignments: variables.map(v => ({ id: v.id, assignedTo: v.assignedTo, type: v.type, variableTitle: v.variableTitle }))
      });
      
      setDynamicFormData(prev => ({ ...prev, ...updates }));
      
      // Also update legacy formData for all field types to ensure document rendering and saving works
      const title = primaryVariable.variableTitle.toLowerCase();
      
      // Map to legacy form fields based on variable title content
      let legacyFieldUpdated = false;
      
      if (primaryVariable.type === 'initial') {
        setFormData(prev => ({ ...prev, initial: value }));
        console.log('Updated legacy formData.initial:', value);
        legacyFieldUpdated = true;
      } else if (primaryVariable.type === 'signature') {
        setFormData(prev => ({ ...prev, signature: value }));
        console.log('Updated legacy formData.signature:', value);
        legacyFieldUpdated = true;
      } else if (title.includes('signer') && title.includes('name')) {
        setFormData(prev => ({ ...prev, signerName: value }));
        console.log('Updated legacy formData.signerName:', value);
        legacyFieldUpdated = true;
      } else if (title.includes('signer') && title.includes('title')) {
        setFormData(prev => ({ ...prev, signerTitle: value }));
        console.log('Updated legacy formData.signerTitle:', value);
        legacyFieldUpdated = true;
      } else if (title.includes('company') && title.includes('name')) {
        setFormData(prev => ({ ...prev, companyName: value }));
        console.log('Updated legacy formData.companyName:', value);
        legacyFieldUpdated = true;
      } else if (title.includes('company') && title.includes('address')) {
        setFormData(prev => ({ ...prev, companyAddress: value }));
        console.log('Updated legacy formData.companyAddress:', value);
        legacyFieldUpdated = true;
      } else if (title.includes('city')) {
        setFormData(prev => ({ ...prev, city: value }));
        console.log('Updated legacy formData.city:', value);
        legacyFieldUpdated = true;
      } else if (title.includes('state')) {
        setFormData(prev => ({ ...prev, state: value }));
        console.log('Updated legacy formData.state:', value);
        legacyFieldUpdated = true;
      } else if (title.includes('zip')) {
        setFormData(prev => ({ ...prev, zipCode: value }));
        console.log('Updated legacy formData.zipCode:', value);
        legacyFieldUpdated = true;
      } else if (title.includes('business') && title.includes('structure')) {
        setFormData(prev => ({ ...prev, businessStructure: value }));
        console.log('Updated legacy formData.businessStructure:', value);
        legacyFieldUpdated = true;
      }
      
      if (!legacyFieldUpdated) {
        console.log('No legacy field mapping found for:', title, primaryVariable.type);
      }
    };
    
    return (
      <div key={primaryVariable.id} className="mb-4 w-full md:w-1/2 pr-2">
        <label htmlFor={fieldId} className={`block text-sm font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
          {primaryVariable.variableTitle}*
          {variables.length > 1 && (
            <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
              ({variables.length} fields)
            </span>
          )}
        </label>
        
        {primaryVariable.type === 'checkbox' ? (
          <div className="flex items-center">
            <input
              id={fieldId}
              type="checkbox"
              checked={currentValue === 'true' || currentValue === true}
              onChange={(e) => {
                handleChange(e.target.checked ? 'true' : 'false');
              }}
              className="mr-2"
              disabled={!isScrolledToBottom}
            />
          </div>
        ) : primaryVariable.variableTitle.toLowerCase().includes('business structure') || 
          primaryVariable.variableTitle.toLowerCase().includes('business type') ? (
          <select
            id={fieldId}
            value={currentValue}
            onChange={(e) => handleChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white"
            disabled={!isScrolledToBottom}
          >
            <option value="">Select business structure</option>
            <option value="Sole Proprietorship">Sole Proprietorship</option>
            <option value="Partnership">Partnership</option>
            <option value="LLC">LLC</option>
            <option value="Corporation">Corporation</option>
            <option value="S-Corporation">S-Corporation</option>
            <option value="Non-Profit">Non-Profit</option>
            <option value="Other">Other</option>
          </select>
        ) : primaryVariable.type === 'date' ? (
          <input
            id={fieldId}
            type="date"
            value={currentValue}
            onChange={(e) => handleChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white"
            disabled={!isScrolledToBottom}
          />
        ) : primaryVariable.type === 'signature' ? (
          <input
            id={fieldId}
            type="text"
            value={currentValue}
            onChange={(e) => handleChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white"
            placeholder="Signature"
            disabled={!isScrolledToBottom}
          />
        ) : primaryVariable.type === 'initial' ? (
          <input
            id={fieldId}
            type="text"
            value={currentValue}
            onChange={(e) => handleChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white"
            placeholder="Initials"
            maxLength="3"
            disabled={!isScrolledToBottom}
          />
        ) : (
          <input
            id={fieldId}
            type="text"
            value={currentValue}
            onChange={(e) => handleChange(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white"
            placeholder={primaryVariable.variableTitle}
            disabled={!isScrolledToBottom}
          />
        )}
      </div>
    );
  };

  // Render template element
  const renderTemplateElement = (element) => {
    if (element.type === 'line') {
      return <div key={element.id} data-element-id={element.id} style={element.style}></div>;
    }
    
    if (element.type === 'header') {
      return (
        <h2 
          key={element.id} 
          data-element-id={element.id}
          style={element.style} 
          className={`text-lg font-semibold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}
        >
          <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(renderContentWithVariables(element.content, element.id)) }} />
        </h2>
      );
    }
    
    if (element.type === 'text') {
      return (
        <div 
          key={element.id} 
          data-element-id={element.id}
          style={element.style} 
          className="mb-3"
        >
          <p dangerouslySetInnerHTML={{ __html: sanitizeHtml(renderContentWithVariables(element.content, element.id)) }} />
          {/* Render sub-items if they exist */}
          {(element.subItems || []).length > 0 && (
            <ul className="list-disc list-inside space-y-1 text-gray-600 dark:text-gray-300 ml-4 mt-2">
              {(element.subItems || []).map((subItem) => (
                <li key={subItem.id} className="text-sm">
                  <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(renderContentWithVariables(subItem.text || 'Empty sub-item', element.id)) }} />
                  {(subItem.subItems || []).length > 0 && (
                    <ul className="list-disc list-inside ml-4 mt-1 space-y-1">
                      {(subItem.subItems || []).map((nestedSubItem) => (
                        <li key={nestedSubItem.id} className="text-sm text-gray-600 dark:text-gray-300">
                          <span dangerouslySetInnerHTML={{ __html: sanitizeHtml(renderContentWithVariables(nestedSubItem.text || 'Empty nested sub-item', element.id)) }} />
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      );
    }
    
    // For input field types assigned to BBA, don't render as form fields
    if (['signature', 'initial', 'text_input', 'checkbox', 'date'].includes(element.type)) {
      if (element.assignedTo === 'BBA') {
        // Show as read-only text for BBA fields
        return (
          <div key={element.id} data-element-id={element.id} className="mb-3">
            <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} dangerouslySetInnerHTML={{ __html: sanitizeHtml(renderContentWithVariables(element.content, element.id)) }} />
          </div>
        );
      }
      // Client fields will be rendered separately in the form section
      return null;
    }
    
    // Handle any other element types that might contain variables
    if (element.content) {
      return (
        <div key={element.id} data-element-id={element.id} className="mb-3">
          <p dangerouslySetInnerHTML={{ __html: sanitizeHtml(renderContentWithVariables(element.content, element.id)) }} />
        </div>
      );
    }
    
    return null;
  };

  const currentDate = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  if (!selectedProgram) {
    return (
      <div className="relative min-h-screen flex items-center justify-center">
        <PortalBackground />
        <div className="relative text-center">
          <BBALoader size={48} className="mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-300">Loading contract...</p>
        </div>
      </div>
    );
  }


  return (
    <div className="relative min-h-screen w-full">
      <PortalBackground />
      <div className="relative max-w-4xl mx-auto p-4">
        <div className="mb-4">
          <BackButton />
        </div>
        
        {/* Audit Trail Viewer Modal */}
        {showAuditTrail && (
          <AuditTrailViewer
            auditData={auditTrailData}
            onClose={() => setShowAuditTrail(false)}
          />
        )}
        <div className={`mb-6 text-center ${darkMode ? 'bg-gray-900' : 'bg-white'}`}>
          <h1 className={`text-2xl font-bold tracking-tight text-gray-900 dark:text-white`}>Service Agreement</h1>
          <p className={`${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Please review and complete the agreement below</p>
          
          {/* E-SIGN Act Compliance Notice */}
          <div className={`mt-4 p-4 rounded-lg border ${darkMode ? 'bg-blue-900/20 border-blue-700' : 'bg-blue-50 border-blue-200'}`}>
            <div className="flex items-center justify-center mb-2">
              <Shield className={`h-5 w-5 mr-2 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
              <span className={`text-sm font-medium ${darkMode ? 'text-blue-300' : 'text-blue-700'}`}>
                E-SIGN Act Compliant Electronic Signature
              </span>
            </div>
            <p className={`text-xs ${darkMode ? 'text-blue-200' : 'text-blue-600'}`}>
              This document will be signed electronically in compliance with the Electronic Signatures in Global and National Commerce Act (E-SIGN Act).
            </p>
          </div>
          </div>

        {!isSigned && (
          <div className={`${darkMode ? 'bg-gray-900' : 'bg-white'}`}>
            {templateLoading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4"></div>
                <p className="text-gray-600 dark:text-gray-400">Loading contract template...</p>
              </div>
            ) : templateError ? (
              <div className="text-center py-12">
                <div className="text-red-600 dark:text-red-400 mb-4">
                  <AlertCircle className="h-8 w-8 mx-auto" />
                </div>
                <p className="text-red-600 dark:text-red-400 text-center mb-4">{templateError}</p>
                <button
                  onClick={() => window.location.reload()}
                  className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                >
                  Retry
                </button>
              </div>
            ) : (
            <>
              {/* Scrollable Document Display */}
              <div className={`border rounded-xl mb-6 border-gray-200 dark:border-gray-800`}>
              <div 
                className={`p-6 h-96 overflow-y-auto text-sm ${darkMode ? 'bg-gray-900 text-gray-300' : 'bg-white text-gray-700'}`}
                onScroll={handleScroll}
              >
                  {templateData && templateData.contractElements && templateData.contractElements.length > 0 ? (
                    templateData.contractElements.map((element) => {
                      return renderTemplateElement(element);
                    })
                  ) : (
                    <div className="text-center py-8">
                      <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                        No Contract Template Found
                      </h3>
                      <p className="text-gray-500 dark:text-gray-400 mb-4">
                        Please create a contract template first before signing.
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Current Input Field */}
              {variablesInOrder.length > 0 && renderCurrentInputField()}
            </>
            )}

            {error && (
              <div className="text-red-600 flex items-center mb-4 text-sm">
                <AlertCircle className="h-4 w-4 mr-2" />
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Form submission is now handled by the Finish button in the combined consent checkbox */}
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default PortalContract;
